/** 产品模板编辑页（独立页面，不复用发布产品页组件） */
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeOptions, optionLabel } from '@/i18n/chartText';
import {
  NET_UNIT_KEYS,
  OUTER_SHAPE_KEYS,
  OUTER_TYPE_KEYS,
  PACKED_KEYS,
  PIECE_UNIT_KEYS,
  SENSITIVE_KEYS,
  SKU_CLASS_KEYS,
  YES_NO_KEYS,
} from '@/i18n/pkgOptionKeys';
import { labeledSiteOptions } from '@/i18n/siteOptions';
import { useNavigate } from 'react-router-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import AttributeField from '@/temu-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import SearchableMultiSelect from '@/temu-platform/modules/products/components/SearchableMultiSelect';
import SpecSupplySection, { skcHasColor } from '@/temu-platform/modules/products/components/SpecSupplySection';
import TemuOfficialSizeChartPanel, {
  fetchSizeChartClassRequired,
} from '@/temu-platform/modules/products/components/TemuOfficialSizeChartPanel';
import TemuModelSelectBar from '@/temu-platform/modules/products/components/TemuModelSelectBar';
import TemuVehicleFitmentPanel from '@/temu-platform/modules/products/components/TemuVehicleFitmentPanel';
import TemuGuideFilePanel from '@/temu-platform/modules/products/components/TemuGuideFilePanel';
import FieldHelpMark from '@/components/FieldHelpMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import { buildTemuModelTryOnSizeOptions } from '@/temu-platform/utils/temuOfficialSizeChart';
import PackageBatchModal, {
  PACKAGE_TYPE_OPTIONS,
} from '@/temu-platform/modules/products/components/PackageBatchModal';
import {
  TEMU_OUTER_PACKAGE_SHAPE_OPTIONS,
  TEMU_OUTER_PACKAGE_TYPE_OPTIONS,
  coerceOuterPackageFormFields,
} from '@/temu-platform/utils/temuOuterPackage';
import {
  normalizeTemuOriginRegion,
  resolveTemuRegion1ShortName,
  TEMU_CN_REGION2_OPTIONS,
  TEMU_REGION1_OPTIONS,
} from '@/temu-platform/utils/temuProductOrigin';
import { localizeRegionOptions, localizeCnProvinceOptions } from '@/i18n/catalogDisplayText';
import {
  getTemuShipmentLimitOptions,
  isTemuCustomProductMode,
  pickTemuShipmentLimitSecond,
} from '@/temu-platform/utils/temuShipmentLimitOptions';
import {
  emptyTemuCustomizedTechnology,
  listTemuCustomFirstTypeOptions,
  listTemuCustomTwiceTypeIds,
  listTemuCustomTwiceTypeOptions,
  TEMU_CUSTOM_TECHNOLOGY_TYPE_OPTIONS,
} from '@/temu-platform/utils/temuCustomizedTechnology';
import PublishImageUploadZone from '@/temu-platform/modules/products/components/PublishImageUploadZone';
import TitleFieldLoadingShell from '@/temu-platform/modules/products/components/TitleFieldLoadingShell';
import {
  TEMU_DIM_UNIT,
  TEMU_INDIVIDUALLY_PACKED_OPTIONS,
  TEMU_PIECE_UNIT_OPTIONS,
  TEMU_SENSITIVE_LIST_OPTIONS,
  TEMU_SENSITIVE_YES_NO_OPTIONS,
  TEMU_SKU_CLASSIFICATION_OPTIONS,
  TEMU_NET_CONTENT_UNIT_OPTIONS,
  TEMU_WEIGHT_UNIT,
  sensitiveListFromSlots,
  sensitiveSlotsFromList,
} from '@/temu-platform/utils/temuSkuPackage';
import { applyPackageBatchBySize, applyPackageBatchToAllSkus } from '@/temu-platform/utils/temuPackageBatch';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import SaveAttributeTemplateMetaModal from '@/temu-platform/modules/templates/SaveAttributeTemplateMetaModal';
import { buildTemuPath, openTemuPathInNewTab } from '@/temu-platform/constants/temuRoutes';
import { isOfficialProductTemplateScope } from '@/temu-platform/constants/temuProductTemplateScope';
import { useTemuShopOptional } from '@/temu-platform/context/TemuShopContext';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { applyShopBrandsToAttrTemplate } from '@/temu-platform/utils/temuShopBrandAttrs';
import { layoutAttributeFields } from '@/temu-platform/utils/temuAttributeLayout';
import {
  isEditorAttrRequiredMark,
  isEditorAttrVisible,
  editorAttrAllowedValueIds,
  pruneProductAttributesForSubmit,
} from '@/temu-platform/utils/temuAttrVisibility';
import { parseCustomAttributePermission } from '@/temu-platform/utils/temuCustomAttribute';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  filterProductAttributesToCategory,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
  alignSkcSizesToSaleAttr,
} from '@/temu-platform/utils/temuProductAttributeTemplate';
import { writeProductTemplatePublishHandoff } from '@/temu-platform/utils/temuProductTemplatePublishHandoff';
import {
  applyPrintNamesToTemuSkcList,
  enrichTemuFormPrintNames,
  printNamesFromTemuPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTemuPublishForm';
import { pickAutoRuleTemplate, filterTemplatesByProductCategory } from '@/temu-platform/utils/temuTemplateAutoRule';
import { nextTemplatePriority } from '@/temu-platform/utils/temuTemplatePriority';
import { styleNoFromSupplierCode } from '@/temu-platform/utils/temuStyleNo';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  isTemplateVersionConflictError,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/temu-platform/utils/temuTemplateChangeDiff';
import TemplateVersionConflictMark from '@/temu-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/temu-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  skuSizeKey,
} from '@/temu-platform/utils/temuSizeChart';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/temu-platform/utils/temuPublishFillInStandard';
import { TEMU_MODE_FULL, TEMU_MODE_SEMI, temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import {
  TEMU_SHOP_TYPE_OPTIONS,
  normalizeTemuShopType,
} from '@/utils/platformShopCatalog';
import {
  officialSizeChartDraftHasContent,
  officialSizeChartRecordsHaveSizeNames,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import { isTemuModelRequiredForCategory } from '@/temu-platform/utils/temuModelRequiredCategories';
import { isTemuNetWeightRequiredForCategory } from '@/temu-platform/utils/temuNetWeightRequiredCategories';
import { isTemuSemiSkuNetContentRequired } from '@/temu-platform/utils/temuSemiNetContentRequiredCategories';
import { isTemuShoeSizeClass } from '@/temu-platform/utils/temuShoeSizeFootMap';
import {
  hasTemuVehicleLibraryData,
  isTemuVehicleLibraryDataRequired,
  isTemuVehicleLibraryScopeActive,
} from '@/temu-platform/utils/temuVehicleLibraryRequired';
import { loadShopPublishCurrencies } from '@/temu-platform/utils/resolveTemuPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/temu-platform/utils/temuSiteList';
import {
  persistTemuFormImageSlots,
  sanitizeTemuFormImageSlots,
} from '@/temu-platform/utils/sanitizeTemuFormImageSlots';
import { normalizeTemuAttrsToEditorShape } from '@/temu-platform/utils/normalizeTemuAttrsForEditor';
import { generateTemuPublishAiTitle, translateTemuTitleToEnglish } from '@/temu-platform/utils/temuPublishAiTitle';
import {
  clipTemuProductNameToLimit,
  countTemuProductNameWeight,
  resolveTemuProductNameLimitForForm,
} from '@/temu-platform/utils/temuProductNameLength';
import { buildCreditActionHint } from '@/constants/ipeCredits';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';

function fetchAttrTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialTemuAttributeTemplate(id);
  }
  return temuPlatformAPI.getProductAttributeTemplate(shopId, id, extra);
}

function fetchAttrTemplateList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialTemuAttributeTemplates(params);
  }
  return temuPlatformAPI.listProductAttributeTemplates(shopId, params);
}

function fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, extra = {}) {
  // TEMU 类目属性必须按叶子 catId 调 bg.goods.attrs.get；勿只用 product_type_id_list 兜底，避免串类目
  const cat = Number(categoryId);
  const typeId = Number(productTypeId);
  const leafCatId = (Number.isFinite(cat) && cat > 0) ? cat : typeId;
  if (!Number.isFinite(leafCatId) || leafCatId <= 0) {
    return Promise.reject(new Error(fixedUi('缺少叶子类目 catId，无法加载类目属性')));
  }
  const body = {
    catId: leafCatId,
    categoryId: leafCatId,
    category_id: leafCatId,
    product_type_id_list: [Number.isFinite(typeId) && typeId > 0 ? typeId : leafCatId],
  };
  if (officialMode) {
    if (extra.shopType) body.shopType = extra.shopType;
    if (extra.categoryPathLabel) body.categoryPathLabel = extra.categoryPathLabel;
    if (extra.referenceShopId) body.referenceShopId = extra.referenceShopId;
    return adminAPI.getOfficialTemuCatalogAttributeTemplate(body).then((raw) => (
      normalizeTemuAttrsToEditorShape(raw, { catId: leafCatId })
    ));
  }
  return temuPlatformAPI.getAttributeTemplate(shopId, body, { forceRefresh: true });
}

function fetchSizeChartTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialTemuSizeChartTemplate(id);
  }
  return temuPlatformAPI.getSizeChartTemplate(shopId, id, extra);
}

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="tpo-field__error" role="alert">{message}</div>;
}

/** 与 TEMU 发品页一致：中国省份（region2Id） */
const CN_REGION2_OPTIONS = TEMU_CN_REGION2_OPTIONS;

/** 半托管常用站点（与官方半托管站点目录 id 对齐） */
const TEMU_SEMI_SITES = [
  ['100', '美国站'],
  ['101', '加拿大站'],
  ['102', '英国站'],
  ['103', '澳大利亚站'],
  ['104', '新西兰站'],
  ['105', '德国站'],
  ['106', '法国站'],
  ['107', '意大利站'],
  ['108', '荷兰站'],
  ['109', '西班牙站'],
  ['110', '墨西哥站'],
  ['118', '日本站'],
  ['125', '智利站'],
  ['163', '秘鲁站'],
  ['164', '哥伦比亚站'],
  ['178', '厄瓜多尔站'],
];

const TEMU_PRODUCT_MODE_OPTIONS = [
  { value: '0', label: '普通品' },
  { value: '1', label: '定制品' },
];

function imageUrlOfSlot(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

const SECTIONS = [
  { id: 'basic', title: '基础信息' },
  { id: 'attrs', title: '类目属性' },
  { id: 'spec', title: '规格供应' },
  { id: 'size', title: '包装信息' },
  { id: 'sales', title: '销售发货' },
];

const SECTION_TITLE_KEYS = {
  basic: 'publishChrome.sectionBasic',
  attrs: 'publishChrome.sectionAttrs',
  spec: 'publishChrome.sectionSpec',
  size: 'publishChrome.sectionSize',
  sales: 'publishChrome.sectionSales',
};

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        netWeight: defaults.netWeight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      netWeight: defaults.netWeight || '',
      currency: defaults.currency || '',
    });
  });
}

function createEmptyAccessoryRow() {
  return { vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' };
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    netWeight: '',
    netContent: '',
    netContentUnitCode: '5',
    length: '',
    width: '',
    height: '',
    quantity: '1',
    quantityUnit: '1',
    numberOfPieces: '1',
    pieceUnitCode: '1',
    totalContains: '',
    skuClassification: '1',
    individuallyPacked: '',
    packageType: '',
    isSensitive: 0,
    sensitiveList: [],
    suggestedPrice: '',
    suggestedPriceCurrencyType: '',
    accessories: [createEmptyAccessoryRow()],
    mallState: 1,
    skuImage: null,
    skuCode: '',
    siteRrpInfoList: [],
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}


function positiveAttrVid(raw) {
  const text = String(raw ?? '').trim();
  if (!text || text === '0') return '';
  const num = Number(text);
  if (Number.isFinite(num) && num <= 0) return '';
  return text;
}

function handInputAttrText(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.propValue ?? row.name ?? row.attributeValueName ?? '').trim();
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        const handTexts = [];
        const rows = [];
        let handOnly = true;
        value.forEach((row) => {
          if (row != null && (typeof row === 'string' || typeof row === 'number')) {
            handOnly = false;
            rows.push({ valueId: String(row), percent: '' });
            return;
          }
          if (!row || typeof row !== 'object') return;
          const valueId = positiveAttrVid(row.valueId ?? row.attributeValueId ?? row.vid);
          if (!valueId) {
            const text = handInputAttrText(row);
            if (text) handTexts.push(text);
            return;
          }
          handOnly = false;
          rows.push({
            valueId,
            percent: String(row.percent ?? row.attributeExtraValue ?? ''),
          });
        });
        if (handOnly) {
          if (handTexts.length === 1) out[id] = handTexts[0];
          else if (handTexts.length > 1) out[id] = handTexts.join(',');
          return;
        }
        out[id] = rows;
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') {
            return positiveAttrVid(row.valueId ?? row.attributeValueId ?? row.vid) || handInputAttrText(row);
          }
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = positiveAttrVid(value.valueId ?? value.attributeValueId ?? value.vid);
      const percent = value.percent ?? value.attributeExtraValue;
      const handText = handInputAttrText(value);
      if (!valueId) {
        if (handText) out[id] = handText;
        else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
          out[id] = String(value.attributeExtraValue);
        }
        return;
      }
      if (percent != null && String(percent).trim() !== '') {
        out[id] = [{ valueId, percent: String(percent) }];
      } else {
        out[id] = valueId;
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const originRegion = normalizeTemuOriginRegion(
    initial.originRegion
    || initial.originCountry
    || initial.productOrigin?.region1ShortName
    || initial.productOrigin?.countryShortName,
  ) || 'CN';
  const originRegion2Raw = initial.originRegion2Id
    ?? initial.region2Id
    ?? initial.productOrigin?.region2Id;
  return {
    spuName: initial.spuName || '',
    title: initial.title || '',
    titleEn: rawTitleEn || '',
    titleLanguage: initial.titleLanguage || 'en',
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    catPathIds: Array.isArray(initial.catPathIds) ? initial.catPathIds : [],
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: originRegion,
    originRegion,
    originRegion2Id: originRegion2Raw != null && String(originRegion2Raw).trim() !== ''
      ? String(originRegion2Raw)
      : '',
    originCertFiles: Array.isArray(initial.originCertFiles) ? initial.originCertFiles : [],
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTemuPrintImages(initial.printImages),
    materialImage: initial.materialImage || null,
    materialImgUrl: initial.materialImgUrl || imageUrlOfSlot(initial.materialImage) || '',
    temuVideos: Array.isArray(initial.temuVideos) ? initial.temuVideos : [],
    guideFiles: Array.isArray(initial.guideFiles) ? initial.guideFiles : [],
    ...(() => {
      const coerced = coerceOuterPackageFormFields(initial);
      return {
        packageShape: coerced.packageShape,
        packageTypeOuter: coerced.packageTypeOuter,
        outerPackageImages: Array.isArray(initial.outerPackageImages)
          ? initial.outerPackageImages
          : [],
      };
    })(),
    freightTemplateId: initial.freightTemplateId || '',
    isCustomProduct: isTemuCustomProductMode(
      initial.isCustomProduct ?? initial.personalizationSwitch,
    ) ? 1 : 0,
    ...(() => {
      const tech = emptyTemuCustomizedTechnology(initial);
      return {
        customFirstType: tech.firstType,
        customTechnologyType: tech.technologyType,
        customTwiceTypes: tech.twiceTypes,
        customManufacturingLocations: tech.manufacturingLocations,
      };
    })(),
    shipmentLimitSecond: initial.shipmentLimitSecond != null
      ? String(initial.shipmentLimitSecond)
      : '172800',
    isSensitive: initial.isSensitive != null ? Number(initial.isSensitive) : 0,
    goodsModels: Array.isArray(initial.goodsModels) ? initial.goodsModels : [],
    goodsVehiclePropertyRelation: (
      initial.goodsVehiclePropertyRelation
      && typeof initial.goodsVehiclePropertyRelation === 'object'
    ) ? initial.goodsVehiclePropertyRelation : null,
    officialSizeChart: initial.officialSizeChart && typeof initial.officialSizeChart === 'object'
      ? initial.officialSizeChart
      : null,
    temuSizeTemplateIds: Array.isArray(initial.temuSizeTemplateIds)
      ? initial.temuSizeTemplateIds.map(String)
      : [],
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    netWeightGrams: initial.netWeightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'temu',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
  };
}

export default function ProductTemplateEditor({
  templateId: initialTemplateId = '',
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  referenceShopId = '',
  afterSaveNavigate,
  onShopTypeChange,
}) {
  const { t, i18n } = useTranslation('workbench');
  const navigate = useNavigate();
  const temuShopCtx = useTemuShopOptional();
  const activeShop = temuShopCtx?.activeShop ?? null;
  const shopId = referenceShopId || activeShop?.id;
  const shopTemplateQuery = useMemo(
    () => (officialMode ? {} : { scope: templateScope }),
    [officialMode, templateScope],
  );
  const shopCaps = useMemo(
    () => temuShopCapabilities(officialMode ? shopType : (activeShop?.shopType || shopType)),
    [officialMode, shopType, activeShop?.shopType],
  );
  const isSemiManaged = shopCaps.mode === TEMU_MODE_SEMI;
  const isFullManaged = shopCaps.mode === TEMU_MODE_FULL;
  // 半托/全托 PA goods.add 都要求 USD，忽略表单里旧默认 CNY
  const forcePublishCurrencyDefault = isSemiManaged || isFullManaged;
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  const officialShopTypeSyncedRef = useRef('');
  const shopTypeRef = useRef(shopType);
  shopTypeRef.current = shopType;
  const onShopTypeChangeRef = useRef(onShopTypeChange);
  onShopTypeChangeRef.current = onShopTypeChange;
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const [templateId, setTemplateId] = useState(() => (
    initialTemplateId ? String(initialTemplateId) : ''
  ));
  const [templateMeta, setTemplateMeta] = useState({
    name: '',
    remark: '',
    autoRuleEnabled: false,
    sortOrder: 0,
    styleNo: '',
  });
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(() => Boolean(initialTemplateId));
  const [templateHydrated, setTemplateHydrated] = useState(() => Boolean(!initialTemplateId));
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** Temu 不走 Shein 关联属性填写规则 */
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);

  const handleOfficialShopTypeChange = useCallback((nextType) => {
    const normalized = normalizeTemuShopType(nextType);
    if (!officialMode || !normalized || normalized === shopType) return;
    setForm((prev) => ({
      ...prev,
      categoryId: '',
      productTypeId: '',
      categoryPathLabel: '',
      productAttributes: {},
      selectedSites: [],
      warehouseId: '',
      warehouseName: '',
      freightTemplateId: '',
      freightTemplateName: '',
    }));
    setAttrTpl(null);
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    showToast(
      templateId
        ? t('templateEditor.shopTypeSwitched', { shopType: normalized })
        : t('templateEditor.shopTypePickedTemu', { shopType: normalized }),
      'info',
    );
    onShopTypeChange?.(normalized);
  }, [officialMode, shopType, templateId, onShopTypeChange, t]);
  const [refVersionConflicts, setRefVersionConflicts] = useState({});
  const [refChangePreviews, setRefChangePreviews] = useState({});
  const [reloadingRefType, setReloadingRefType] = useState('');
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；货号变更时重匹配 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);
  const [freightOptions, setFreightOptions] = useState([]);
  const [freightLoading, setFreightLoading] = useState(false);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [accessoryLoading, setAccessoryLoading] = useState(false);
  const [accessoryLoadError, setAccessoryLoadError] = useState('');
  const [accessoryReloadKey, setAccessoryReloadKey] = useState(0);
  const [needGuideFile, setNeedGuideFile] = useState(false);
  const [needGuideLoading, setNeedGuideLoading] = useState(false);
  /** 官方尺码表：bg.goods.sizecharts.class.get（对齐发品页） */
  const [sizeChartRequired, setSizeChartRequired] = useState(false);
  const [sizeChartClass, setSizeChartClass] = useState(null);
  const [sizeChartClassLoading, setSizeChartClassLoading] = useState(false);
  const [sizeChartClassError, setSizeChartClassError] = useState('');
  const officialSizeChartRef = useRef(null);
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [titleTranslateBusy, setTitleTranslateBusy] = useState(false);
  const [originCertBusy, setOriginCertBusy] = useState(false);
  const titleTranslateSeqRef = useRef(0);
  const lastAutoTitleEnRef = useRef('');

  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);
  const siteSelectOptions = useMemo(() => {
    void i18n.language;
    if (isSemiManaged) {
      return labeledSiteOptions('temuSite', TEMU_SEMI_SITES);
    }
    return siteOptions.map((site) => ({
      value: site.siteAbbr,
      label: site.siteName || site.siteAbbr,
    }));
  }, [isSemiManaged, siteOptions, i18n.language]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    return pickPublishCurrency(publishCurrencies, form.currency, {
      forceDefault: forcePublishCurrencyDefault,
      defaultCode: forcePublishCurrencyDefault ? 'USD' : '',
    });
  }, [form.currency, form.selectedSites, forcePublishCurrencyDefault, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  const shipmentLimitOptions = useMemo(
    () => getTemuShipmentLimitOptions({
      siteIds: selectedSitesKey ? selectedSitesKey.split('|') : [],
      customProduct: form.isCustomProduct,
    }),
    [selectedSitesKey, form.isCustomProduct],
  );

  const customFirstTypeOptions = useMemo(
    () => listTemuCustomFirstTypeOptions(form.customTechnologyType),
    [form.customTechnologyType],
  );
  const customTwiceTypeOptions = useMemo(
    () => listTemuCustomTwiceTypeOptions(form.customTechnologyType, form.customFirstType),
    [form.customTechnologyType, form.customFirstType],
  );
  const customTwiceAllowedIds = useMemo(
    () => listTemuCustomTwiceTypeIds(form.customTechnologyType, form.customFirstType),
    [form.customTechnologyType, form.customFirstType],
  );
  const customTwiceRequired = Array.isArray(customTwiceAllowedIds) && customTwiceAllowedIds.length > 0;

  useEffect(() => {
    if (!isSemiManaged || !shipmentLimitOptions.length) return;
    const next = pickTemuShipmentLimitSecond(form.shipmentLimitSecond, shipmentLimitOptions);
    if (!next || String(form.shipmentLimitSecond || '') === next) return;
    setForm((prev) => ({ ...prev, shipmentLimitSecond: next }));
  }, [isSemiManaged, shipmentLimitOptions, form.shipmentLimitSecond]);

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency, {
        forceDefault: forcePublishCurrencyDefault,
        defaultCode: forcePublishCurrencyDefault ? 'USD' : '',
      });
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo, forcePublishCurrencyDefault]);

  const warehouseOptions = useMemo(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      const fallbackName = String(form.warehouseName || '').trim();
      mapped.unshift({
        value: String(form.warehouseId),
        label: fallbackName || String(form.warehouseId),
        name: fallbackName || String(form.warehouseId),
      });
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：默认第一个。编辑回填可能清空 warehouseId，需在列表就绪或选中失效时再默认一次。
  // 全托管/本土产品模板跳过站点·仓·运费，不自动写入 warehouseId。
  useEffect(() => {
    if (skipSiteWarehouseFreight) return;
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId, skipSiteWarehouseFreight]);

  // 运费模板（半托发品 productShipmentReq 必填；全托/本土跳过，不预加载）
  useEffect(() => {
    if (!shopId || officialMode || skipSiteWarehouseFreight) {
      setFreightOptions([]);
      setFreightLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFreightLoading(true);
    temuPlatformAPI.listShopFreightTemplates(shopId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.items)
          ? res.items
          : (Array.isArray(res?.freightTemplates) ? res.freightTemplates : []);
        setFreightOptions(rows.map((t) => {
          const id = String(t.freightTemplateId || t.templateId || t.id || '').trim();
          const name = String(t.templateName || t.name || id).trim();
          return { value: id, label: name || id };
        }).filter((t) => t.value));
      })
      .catch(() => {
        if (!cancelled) setFreightOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFreightLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, skipSiteWarehouseFreight]);

  // 包装清单选项（发品下拉：accessories.get；选类目后再拉，避免未选类目就出现加载提示）
  // 官方模式借同类型店拉取，无借店时仅提示不硬拦保存
  useEffect(() => {
    if (!form.categoryId) {
      setAccessoryOptions([]);
      setAccessoryLoadError('');
      setAccessoryLoading(false);
      return undefined;
    }
    if (officialMode) {
      if (!shopType) {
        setAccessoryOptions([]);
        setAccessoryLoadError('');
        setAccessoryLoading(false);
        return undefined;
      }
    } else if (!shopId) {
      setAccessoryOptions([]);
      setAccessoryLoadError('');
      setAccessoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAccessoryLoading(true);
    setAccessoryLoadError('');
    const loadPromise = officialMode
      ? adminAPI.getOfficialTemuCatalogAccessories({
        shopType,
        referenceShopId: shopId || undefined,
        page: 1,
        pageSize: 100,
      })
      : temuPlatformAPI.listPublishAccessories(shopId, { page: 1, pageSize: 100 });
    loadPromise
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.result?.data) ? res.result.data : []);
        const opts = (rows || []).map((row) => ({
          value: String(row.vid),
          label: String(row.value || row.vid),
          unitCode: Number(row.unitCode || 1),
          unitName: String(row.unitName || '件'),
          thumbUrl: String(row.thumbUrl || ''),
        })).filter((o) => o.value);
        setAccessoryOptions(opts);
        if (!opts.length) {
          setAccessoryLoadError(officialMode
            ? t('templateEditor.accBorrowEmpty')
            : t('publishPkg.accEmpty'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAccessoryOptions([]);
        const msg = String(e?.message || e?.userMessage || t('publishPkg.accLoadFail')).trim();
        setAccessoryLoadError(msg);
        showToast(officialMode
          ? t('templateEditor.accBorrowFail', { message: msg })
          : t('publishPkg.accLoadFailWrap', { message: msg }), 'warning');
      })
      .finally(() => {
        if (!cancelled) setAccessoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, form.categoryId, accessoryReloadKey, t]);

  // 类目是否必传说明书（官方模式无店时跳过）
  useEffect(() => {
    const catId = Number(form.categoryId || 0);
    if (officialMode || !shopId || !(catId > 0)) {
      setNeedGuideFile(false);
      setNeedGuideLoading(false);
      return undefined;
    }
    let cancelled = false;
    setNeedGuideLoading(true);
    temuPlatformAPI.getPublishCatsMandatory(shopId, {
      leafCatId: catId,
      configItems: [1],
      bindSiteIds: isSemiManaged
        ? (form.selectedSites || []).map((id) => Number(id)).filter((id) => id > 0)
        : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const flag = Boolean(
          res?.needGuideFile
          ?? res?.result?.needGuideFile
          ?? false,
        );
        setNeedGuideFile(flag);
      })
      .catch(() => {
        if (!cancelled) setNeedGuideFile(false);
      })
      .finally(() => {
        if (!cancelled) setNeedGuideLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, form.categoryId, isSemiManaged, form.selectedSites]);

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []),
    [sizeMeasureAttrsBase],
  );
  const selectedSizeLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    const push = (key, label) => {
      const k = String(key || '').trim();
      const name = String(label || '').trim();
      if (!k || seen.has(k) || !name) return;
      seen.add(k);
      labels.push(name);
    };
    (form.skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        const key = skuSizeKey(sku);
        const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId));
        push(
          key,
          sku.sizeLabel || sku.sizeExtraValue || fromOpt?.name || attributeValueDisplayLabel(fromOpt),
        );
      });
    });
    (form.selectedSizeIds || []).forEach((id) => {
      const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(id));
      push(id, fromOpt?.name || attributeValueDisplayLabel(fromOpt) || id);
    });
    return labels;
  }, [form.skcList, form.selectedSizeIds, sizeAttr]);
  const modelTryOnSizeOptions = useMemo(() => buildTemuModelTryOnSizeOptions({
    sizeAttr,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  }), [sizeAttr, attrTpl, form.selectedSizeIds, form.skcList]);
  /** 部分叶子类目模特必填（平台公告清单，与尺码表必填独立）；官方模板可留空 */
  const modelRequired = useMemo(
    () => !officialMode && isTemuModelRequiredForCategory(form.categoryId),
    [form.categoryId, officialMode],
  );
  /** 发品模特类型由类目锁定：鞋类尺码表 → 鞋类模特，其余（含服装）→ 成衣模特 */
  const publishModelType = useMemo(
    () => (isTemuShoeSizeClass(sizeChartClass?.classId) ? '1' : '0'),
    [sizeChartClass?.classId],
  );
  /** 部分叶子类目货品净重必填 → productSkuNetWeightReq（页面 g，提交转 mg） */
  const netWeightRequired = useMemo(
    () => isTemuNetWeightRequiredForCategory(form.categoryId),
    [form.categoryId],
  );
  const resolvedShopType = officialMode ? shopType : (activeShop?.shopType || shopType);
  /** 半托管 + 清单类目：SKU 分类与净含量必填 */
  const semiSkuNetContentRequired = useMemo(
    () => isTemuSemiSkuNetContentRequired({
      categoryId: form.categoryId,
      shopType: resolvedShopType,
    }),
    [form.categoryId, resolvedShopType],
  );
  const vehicleLibraryScopeActive = useMemo(
    () => isTemuVehicleLibraryScopeActive({
      categoryId: form.categoryId,
      shopType: resolvedShopType,
      selectedSites: form.selectedSites,
    }),
    [form.categoryId, resolvedShopType, form.selectedSites],
  );
  const vehicleLibraryRequired = useMemo(
    () => isTemuVehicleLibraryDataRequired({
      categoryId: form.categoryId,
      shopType: resolvedShopType,
      selectedSites: form.selectedSites,
      attrTpl,
      productAttributes: form.productAttributes,
    }),
    [
      form.categoryId,
      resolvedShopType,
      form.selectedSites,
      attrTpl,
      form.productAttributes,
    ],
  );
  /** 货品名称加权上限：按一级类目 cat1Id（公告清单，默认 500） */
  const titleMaxWeight = useMemo(
    () => resolveTemuProductNameLimitForForm({
      catPathIds: form.catPathIds,
      categoryPathIds: form.categoryPathIds,
      cat1Id: form.cat1Id,
    }),
    [form.catPathIds, form.categoryPathIds, form.cat1Id],
  );
  const titleWeight = useMemo(
    () => countTemuProductNameWeight(form.title),
    [form.title],
  );
  const titleEnWeight = useMemo(
    () => countTemuProductNameWeight(form.titleEn),
    [form.titleEn],
  );
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(() => calcPublishCompletion(form, attrTpl), [form, attrTpl]);

  const onOfficialSizeChartDraftChange = useCallback((draft) => {
    setForm((prev) => {
      const prevDraft = prev.officialSizeChart;
      const nextDraft = draft && typeof draft === 'object' ? draft : null;
      if (prevDraft === nextDraft) return prev;
      // 禁止 loading/空壳 draft 冲掉已回填的测量值
      const nextGroupList = nextDraft?.groupList || prevDraft?.groupList || [];
      if (
        officialSizeChartDraftHasContent(prevDraft, prevDraft?.groupList)
        && !officialSizeChartDraftHasContent(nextDraft, nextGroupList)
        && !officialSizeChartRecordsHaveSizeNames(nextDraft?.records, nextGroupList)
      ) {
        return prev;
      }
      if (
        prevDraft
        && nextDraft
        && String(prevDraft.mode || '') === String(nextDraft.mode || '')
        && String(prevDraft.selectedLibraryId || '') === String(nextDraft.selectedLibraryId || '')
        && Number(prevDraft.sizeClass?.classId || 0) === Number(nextDraft.sizeClass?.classId || 0)
        && JSON.stringify(prevDraft.selectedBusinessIds || []) === JSON.stringify(nextDraft.selectedBusinessIds || [])
        && JSON.stringify((prevDraft.records || []).map((row) => row?.values || {}))
        === JSON.stringify((nextDraft.records || []).map((row) => row?.values || {}))
      ) {
        return prev;
      }
      return { ...prev, officialSizeChart: nextDraft };
    });
  }, []);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => {
      const draft = { ...prev.productAttributes, [attributeId]: value };
      const pool = [
        ...(attrTpl?.productAttrs || []),
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.optionalProduct || []),
      ];
      const seen = new Set();
      const allAttrs = pool.filter((a) => {
        const id = String(a?.attributeId || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const pruned = pruneProductAttributesForSubmit(draft, allAttrs);
      return { ...prev, productAttributes: pruned };
    });
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    const names = printNamesFromTemuPrintImages(list);
    // 印花图不提交平台；个人/官方模板上传后都把图案编号拼进货号
    setForm((prev) => ({
      ...prev,
      printImages: list,
      ipePrintImageNames: names,
      skcList: applyPrintNamesToTemuSkcList(prev.skcList, names),
    }));
  };

  const onMaterialImagesChange = (next) => {
    // PublishImageUploadZone 单图模式传对象/null；多图模式传数组
    const img = Array.isArray(next) ? (next[0] || null) : (next || null);
    setForm((prev) => ({
      ...prev,
      materialImage: img,
      materialImgUrl: String(img?.temuUrl || '').trim(),
    }));
  };

  const onVideosChange = (videos) => {
    setForm((prev) => ({
      ...prev,
      temuVideos: Array.isArray(videos) ? videos : [],
    }));
    setFieldErrors((prev) => {
      if (!prev?.temuVideos) return prev;
      const next = { ...prev };
      delete next.temuVideos;
      return next;
    });
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuRuleDone'), 'success');
      return;
    }
    const next = {};
    [
      'price',
      'suggestedPrice',
      'suggestedPriceCurrencyType',
      'stock',
      'supplierSku',
      'weight',
      'netWeight',
      'length',
      'width',
      'height',
      'quantity',
      'quantityUnit',
      'packageType',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    netWeight: prev.netWeightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    const safeKeys = [...new Set((colorKeys || []).map(String).filter((key) => {
      if (!key) return false;
      if (key.startsWith('custom:')) return Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      // 一条旧 SKC 只能挂到一个仍选中的颜色上。删掉的颜色不在 safeKeys 里，
      // 其包装行会随 SKC 一起丢掉，避免和新颜色共用 id 后表格行重叠。
      const usedSkcIds = new Set();
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (!skc?.id || usedSkcIds.has(skc.id)) return false;
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          usedSkcIds.add(existing.id);
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => {
      if (isCustomSpecKey(id)) return Boolean(customLabelFromKey(id));
      return allowed.has(id);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (!initialTemplateId) {
      setTemplateLoading(false);
      setTemplateHydrated(true);
      return undefined;
    }
    if (!officialMode && !shopId) {
      return undefined;
    }
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateHydrated(false);
    const loadPromise = officialMode
      ? adminAPI.getOfficialTemuProductTemplate(initialTemplateId).then((res) => res?.item || res?.data?.item || res)
      : temuPlatformAPI.getProductTemplate(shopId, initialTemplateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      }).then((res) => res?.item || res);
    loadPromise
      .then((item) => {
        if (cancelled || !item) return;
        const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        const formData = rawPayload.form && typeof rawPayload.form === 'object'
          ? rawPayload.form
          : rawPayload;
        setTemplateId(String(item.id || initialTemplateId));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        setTemplateMeta({
          name: String(item.name || '').trim(),
          remark: String(item.remark || '').trim(),
          autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
          sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
          styleNo: String(item.styleNo || '').trim(),
        });
        setForm(sanitizeTemuFormImageSlots(emptyForm({
          ...formData,
          // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
          categoryId: item.categoryId || formData.categoryId || '',
          productTypeId: item.productTypeId || formData.productTypeId || '',
          categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
        })));
        setSelectedAttrTemplateId(String(
          rawPayload.selectedAttrTemplateId
          || formData.selectedAttrTemplateId
          || '',
        ).trim());
        setSelectedSizeChartTemplateId(String(
          rawPayload.selectedSizeChartTemplateId
          || formData.selectedSizeChartTemplateId
          || '',
        ).trim());
        setSelectedAttrTemplateVersion(parseTemplateVersion(
          rawPayload.selectedAttrTemplateVersion
          ?? formData.selectedAttrTemplateVersion,
        ));
        setSelectedSizeChartTemplateVersion(parseTemplateVersion(
          rawPayload.selectedSizeChartTemplateVersion
          ?? formData.selectedSizeChartTemplateVersion,
        ));
        setRefVersionConflicts({});
        if (officialMode) {
          const rowType = normalizeTemuShopType(item.shopType);
          if (rowType) {
            const syncKey = `${initialTemplateId}:${rowType}`;
            if (officialShopTypeSyncedRef.current !== syncKey) {
              officialShopTypeSyncedRef.current = syncKey;
              if (rowType !== shopTypeRef.current) {
                onShopTypeChangeRef.current?.(rowType);
              }
            }
          }
        }
        setTemplateHydrated(true);
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
        setTemplateHydrated(true);
        navigate(
          afterSaveNavigate || buildTemuPath('templates/product'),
          { replace: true },
        );
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => { cancelled = true; };
  }, [initialTemplateId, shopId, officialMode, templateScope, navigate, afterSaveNavigate, t]);

  // 校验已套用属性库仍属于当前范围；官方复制残留 ID 会 404，解开引用但保留已填写属性
  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    fetchAttrTemplateDetail(officialMode, shopId, attrId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedAttrTemplateId, templateScope, shopTemplateQuery]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    fetchSizeChartTemplateDetail(officialMode, shopId, sizeId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedSizeChartTemplateId, templateScope, shopTemplateQuery]);

  useEffect(() => {
    if (!(officialMode ? shopType : shopId) || !form.categoryId) {
      setSizeChartRequired(false);
      setSizeChartClass(null);
      setSizeChartClassError('');
      setSizeChartClassLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSizeChartClassLoading(true);
    setSizeChartClassError('');
    fetchSizeChartClassRequired(shopId, form.categoryId, { officialMode, shopType })
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setSizeChartRequired(false);
          setSizeChartClass(null);
          setSizeChartClassError(res?.error || res?.message || t('templateEditor.sizeClassFailShort'));
          showToast(res?.error || res?.message || t('templateEditor.sizeClassFail'), 'error');
          return;
        }
        setSizeChartRequired(Boolean(res.required));
        setSizeChartClass(res.sizeClass || null);
        setSizeChartClassError('');
        if (!res.required) {
          setForm((prev) => (
            (prev.temuSizeTemplateIds || []).length
              ? { ...prev, temuSizeTemplateIds: [] }
              : prev
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setSizeChartClassLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, officialMode, shopType, t]);

  useEffect(() => {
    if (officialMode) {
      if (!shopType) {
        setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
        setPublishCurrencies([]);
        return undefined;
      }
      let cancelled = false;
      setMetaLoading(true);
      setPublishCurrencyLoading(false);
      Promise.all([
        adminAPI.getOfficialTemuCatalogCategories({ shopType }),
        adminAPI.getOfficialTemuCatalogSites({ shopType }).catch(() => null),
        adminAPI.getOfficialTemuCatalogWarehouses({ shopType }).catch(() => ({ warehouses: [] })),
      ])
        .then(([catRes, siteRes, warehouseRes]) => {
          if (cancelled) return;
          const warehouses = Array.isArray(warehouseRes?.warehouses)
            ? warehouseRes.warehouses
            : [];
          setMeta({
            categories: catRes?.categories ?? null,
            brands: null,
            ips: null,
            sites: siteRes?.siteList ?? siteRes ?? null,
            warehouses,
          });
          setPublishCurrencies([]);
        })
        .catch((e) => {
          if (cancelled) return;
          setMeta({
            categories: null,
            brands: null,
            ips: null,
            sites: null,
            warehouses: [],
          });
          setPublishCurrencies([]);
          showToast(e?.message || t('templateEditor.categoryLoadFail'), 'error');
        })
        .finally(() => {
          if (!cancelled) setMetaLoading(false);
        });
      return () => { cancelled = true; };
    }
    if (!shopId) {
      setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      temuPlatformAPI.getShopCategories(shopId),
      temuPlatformAPI.getShopBrands(shopId),
      temuPlatformAPI.getShopIps(shopId),
      temuPlatformAPI.getShopSiteList(shopId),
      temuPlatformAPI.getShopWarehouses(shopId).catch(() => ({ warehouses: [] })),
      temuPlatformAPI.getPublishFillInStandard(shopId, {}).catch(() => null),
    ])
      .then(async ([catRes, brandRes, ipRes, siteRes, warehouseRes, fillInRes]) => {
        if (cancelled) return;
        const warehouses = Array.isArray(warehouseRes?.warehouses)
          ? warehouseRes.warehouses
          : [];
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: fillInRes,
          refreshSiteList: () => temuPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          brands: brandRes?.brands ?? null,
          ips: ipRes?.ips ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        if (loaded.emptyWarning) {
          // eslint-disable-next-line no-console
          console.warn('[temu] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopCaps, shopType, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (officialMode && !shopType) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (!form.productTypeId) {
      setAttrTpl(null);
      setAttrError(t('templateEditor.noProductType'));
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    fetchCategoryAttributeSchema(officialMode, shopId, form.productTypeId, form.categoryId, {
      shopType,
      categoryPathLabel: form.categoryPathLabel,
      referenceShopId: officialMode ? shopId : undefined,
    })
      .then(async (res) => {
        if (cancelled) return;
        let brandsRaw = null;
        if (shopId && !officialMode) {
          try {
            const brandRes = await temuPlatformAPI.getShopBrands(shopId);
            brandsRaw = brandRes?.brands ?? brandRes;
          } catch {
            brandsRaw = null;
          }
        }
        const parsed = applyShopBrandsToAttrTemplate(
          parseAttributeTemplate(res, { productTypeId: form.productTypeId }),
          brandsRaw,
        );
        if (parsed?.meta) {
          parsed.meta.catId = String(form.categoryId || form.productTypeId || '');
        }
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            productAttributes: alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed),
            sizeChart: remappedSize.sizeChart,
            selectedSizeIds,
            skcList: remappedSkc.skcList,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.productTypeId, form.categoryPathLabel, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !(form.productTypeId || form.categoryId)) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免编辑回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId || '').trim();
    const categoryId = String(form.categoryId || '').trim();
    const queryKeys = [...new Set([productTypeId, categoryId].filter(Boolean))];
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    Promise.all(queryKeys.map((id) => fetchAttrTemplateList(officialMode, shopId, {
      productTypeId: id,
      page: 1,
      pageSize: 100,
      ...(officialMode ? { shopType } : { scope: templateScope }),
    }).catch(() => ({ items: [] }))))
      .then((results) => {
        if (cancelled) return;
        const merged = [];
        const seen = new Set();
        results.forEach((res) => {
          (Array.isArray(res?.items) ? res.items : []).forEach((item) => {
            const id = String(item?.id || '').trim();
            if (!id || seen.has(id)) return;
            seen.add(id);
            merged.push(item);
          });
        });
        setAttrTemplates(filterTemplatesByProductCategory(merged, {
          productTypeId,
          categoryId,
          categoryPathLabel: form.categoryPathLabel,
        }));
        attrTemplatesLoadedForRef.current = productTypeId || categoryId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId || categoryId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, form.productTypeId, form.categoryId, form.categoryPathLabel, templateScope]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取发布规范（quantity_info / package_type 必须带 category_id）
  useEffect(() => {
    if (!form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (!officialMode && !shopId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (officialMode && !shopType) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    const fillInPromise = officialMode
      ? adminAPI.getOfficialTemuCatalogPublishFillInStandard({
        shopType,
        category_id: Number(categoryId),
        categoryPathLabel: form.categoryPathLabel || '',
        product_type_id: form.productTypeId || undefined,
      }, { shopId: shopId || undefined })
      : temuPlatformAPI.getPublishFillInStandard(shopId, {
        category_id: Number(categoryId),
      });
    fillInPromise
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.categoryPathLabel, form.productTypeId]);

  const sizeBindKey = (form.skcList || []).map((skc) => (
    (skc.skuRows || []).map((row) => (
      `${row.sizeValueId || ''}:${row.sizeLabel || ''}:${row.sizeExtraValue || ''}:${row.sizeIsCustom ? 1 : 0}`
    )).join(',')
  )).join(';');

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        netWeight: prev.netWeightGrams || '',
        currency: prev.currency || '',
      };
      // 供应表已有尺码名时，按类目销售属性挂回主规格；不要丢掉对不上 valueId 的尺码。
      const alignedSizes = alignSkcSizesToSaleAttr(prev.skcList || [], sizeAttr);
      const rowSizeIds = (alignedSizes.selectedSizeIds || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => !sizeValues.length || sizeIds.has(id) || isCustomSpecKey(id));
      const nextSkcList = (prev.skcList || []).map((skc, index) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = alignedSizes.skcList[index]?.skuRows || skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => skcHasColor(skc));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
                && String(row.sizeValueId || '') === String(n.skuRows[j]?.sizeValueId || '')
                && Boolean(row.sizeIsCustom) === Boolean(n.skuRows[j]?.sizeIsCustom)
                && String(row.sizeLabel || '') === String(n.skuRows[j]?.sizeLabel || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr, sizeAttr, sizeBindKey]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const needType = !form.productTypeId && selected.productTypeId;
    const needPath = !form.categoryPathLabel && selected.label;
    const needCatPath = !(Array.isArray(form.catPathIds) && form.catPathIds.length)
      && Array.isArray(selected.pathIds) && selected.pathIds.length;
    if (!needType && !needPath && !needCatPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      return {
        ...prev,
        productTypeId: prev.productTypeId || (selected.productTypeId ? String(selected.productTypeId) : ''),
        categoryPathLabel: prev.categoryPathLabel || selected.label || '',
        catPathIds: (Array.isArray(prev.catPathIds) && prev.catPathIds.length)
          ? prev.catPathIds
          : (Array.isArray(selected.pathIds) ? selected.pathIds.map(String) : []),
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, form.catPathIds, categoryOptions]);

  const onCategoryChange = (categoryId, meta) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = selected?.productTypeId
      ? String(selected.productTypeId)
      : (meta?.productTypeId ? String(meta.productTypeId) : nextCategoryId);
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setRefVersionConflicts({});
    setRefChangePreviews({});
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: selected?.label || meta?.label || '',
      catPathIds: Array.isArray(selected?.pathIds)
        ? selected.pathIds.map(String)
        : (Array.isArray(meta?.pathIds) ? meta.pathIds.map(String) : []),
      productAttributes: {},
      sizeChart: {},
      skcList: [],
      selectedSizeIds: [],
    }));
    setAttrsExpanded(false);
    if (!templateId && nextProductTypeId && (officialMode ? shopType : shopId)) {
      const loadPriority = officialMode
        ? adminAPI.listOfficialTemuProductTemplates({
          productTypeId: nextProductTypeId,
          shopType,
          page: 1,
          pageSize: 100,
        })
        : temuPlatformAPI.listProductTemplates(shopId, {
          productTypeId: nextProductTypeId,
          page: 1,
          pageSize: 100,
        });
      loadPriority.then((res) => {
        setTemplateMeta((prev) => ({
          ...prev,
          sortOrder: nextTemplatePriority(res?.items),
        }));
      }).catch(() => {
        /* ignore */
      });
    }
  };

  const onSitesChange = (abbr) => {
    const next = abbr == null || abbr === '' ? '' : String(abbr);
    const selected = next ? [next] : [];
    const first = siteOptions.find((s) => selected.includes(s.siteAbbr));
    const nextShipmentOptions = getTemuShipmentLimitOptions({
      siteIds: selected,
      customProduct: form.isCustomProduct,
    });
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'temu',
      shipmentLimitSecond: pickTemuShipmentLimitSecond(
        prev.shipmentLimitSecond,
        nextShipmentOptions,
      ),
      // 自运营币种随已选站点由 pickSiteListCurrency 回写；半托管/全托管仍走发布规范
    }));
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`tpo-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      shopType: formSource.shopType || (officialMode ? shopType : '') || activeShop?.shopType || '',
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
      // 全托管/本土：模板不落站点/仓/运费，避免套用发品时误传
      ...(skipSiteWarehouseFreight
        ? {
          selectedSites: [],
          warehouseId: '',
          warehouseName: '',
          freightTemplateId: '',
          shipmentLimitSecond: '',
          temuSiteIds: [],
          temuSiteId: '',
          temuFreightTemplateId: '',
        }
        : {}),
    };
  };

  const reloadTemplateFromServer = async () => {
    if (!shopId || !templateId) return;
    setReloadingVersion(true);
    try {
      const res = await temuPlatformAPI.getProductTemplate(shopId, templateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      });
      const item = res?.item || res || {};
      const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      const formData = rawPayload.form && typeof rawPayload.form === 'object'
        ? rawPayload.form
        : rawPayload;
      setTemplateVersion(parseTemplateVersion(item.version));
      setTemplateMeta({
        name: String(item.name || '').trim(),
        remark: String(item.remark || '').trim(),
        autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
        sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
        styleNo: String(item.styleNo || '').trim(),
      });
      setForm(sanitizeTemuFormImageSlots(emptyForm({
        ...formData,
        // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
        categoryId: item.categoryId || formData.categoryId || '',
        productTypeId: item.productTypeId || formData.productTypeId || '',
        categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
      })));
      setSelectedAttrTemplateId(String(
        rawPayload.selectedAttrTemplateId
        || formData.selectedAttrTemplateId
        || '',
      ).trim());
      setSelectedSizeChartTemplateId(String(
        rawPayload.selectedSizeChartTemplateId
        || formData.selectedSizeChartTemplateId
        || '',
      ).trim());
      setSelectedAttrTemplateVersion(parseTemplateVersion(
        rawPayload.selectedAttrTemplateVersion
        ?? formData.selectedAttrTemplateVersion,
      ));
      setSelectedSizeChartTemplateVersion(parseTemplateVersion(
        rawPayload.selectedSizeChartTemplateVersion
        ?? formData.selectedSizeChartTemplateVersion,
      ));
      setRefVersionConflicts({});
      setVersionConflict(false);
      showToast(t('templateEditor.reloaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const clearRefVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setRefVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setRefChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.attrUpdated'), changes: [] };
    }
    const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const payload = item.payload || {};
    const { patch } = applyProductAttributeTemplatePayload(
      payload,
      (allProductAttrs || []).map((attr) => String(attr.attributeId || '')).filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate(
      { ...patch },
      attrTplRef.current || attrTpl,
    );
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs: [
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.mainAttrs || []),
        ...(attrTpl?.otherAttrs || []),
      ],
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.sizeUpdated'), changes: [] };
    }
    const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const reloadReferencedTemplateRegion = async (type) => {
    if (!officialMode && !shopId) return;
    setReloadingRefType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const item = res?.item || {};
        const patch = extractAttrTemplatePatch(item);
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const patch = extractSizeChartTemplatePatch(res?.item || {});
        if (patch.kind === 'temu-official' || patch.officialSizeChart) {
          setForm((prev) => ({
            ...prev,
            officialSizeChart: patch.officialSizeChart,
            temuSizeTemplateIds: (patch.platformBusinessIds || []).map(String),
          }));
        } else {
          applySizeChart(patch.sizeChart || {}, { replace: true });
        }
        setSelectedSizeChartTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedSize'), 'success');
      }
    } catch (e) {
      if (!officialMode && isMissingTemplateError(e)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearRefVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('templateEditor.refSizeOut')
            : t('templateEditor.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingRefType('');
    }
  };

  const onSaveProductTemplate = async () => {
    if (!officialMode && !shopId) {
      showToast(t('templateEditor.pickShopSave'), 'warning');
      return;
    }
    if (officialMode && !shopType) {
      showToast(t('templateEditor.pickShopTypeTemu'), 'warning');
      return;
    }
    const name = String(templateMeta.name || '').trim();
    if (!name) {
      showToast(t('templateEditor.fillName'), 'error');
      setActiveSection('basic');
      return;
    }
    // TEMU 发品无商家货号；款号作站内模板检索/自动规则键，须单独填写
    const styleNo = String(templateMeta.styleNo || '').trim().slice(0, 128);
    if (!styleNo) {
      showToast(t('templateEditor.fillStyle'), 'error');
      setActiveSection('basic');
      return;
    }
    if (!officialMode && (!form.categoryId || !form.productTypeId)) {
      showToast(t('templateEditor.pickCategory'), 'error');
      setActiveSection('basic');
      return;
    }
    setSavingTemplate(true);
    try {
      let nextAttrTemplateId = String(selectedAttrTemplateId || '').trim();
      let nextAttrTemplateVersion = selectedAttrTemplateVersion;
      let nextSizeChartTemplateId = String(selectedSizeChartTemplateId || '').trim();
      let nextSizeChartTemplateVersion = selectedSizeChartTemplateVersion;
      if (!officialMode && shopId) {
        // 保存前校验已套用的属性库 / 尺码库版本，避免把脏引用写进产品模板
        const refCheck = await checkTemplateVersions(
          temuPlatformAPI,
          shopId,
          collectAppliedTemplateRefs({
            attrTemplateId: nextAttrTemplateId,
            attrTemplateVersion: nextAttrTemplateVersion,
            sizeChartTemplateId: nextSizeChartTemplateId,
            sizeChartTemplateVersion: nextSizeChartTemplateVersion,
          }).filter((ref) => ref.version != null),
        );
        if (!refCheck.ok) {
          const conflicts = refCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                nextAttrTemplateId = '';
                nextAttrTemplateVersion = null;
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                nextSizeChartTemplateId = '';
                nextSizeChartTemplateVersion = null;
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
            const nextMarks = {};
            blocking.forEach((c) => {
              const key = conflictFieldKey(c.type);
              if (key) nextMarks[key] = true;
            });
            setRefVersionConflicts(nextMarks);
            const nextPreviews = {};
            await Promise.all(blocking.map(async (c) => {
              try {
                if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                  const preview = await buildAttrChangePreview();
                  nextPreviews.productAttributes = preview;
                } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                  const preview = await buildSizeChartChangePreview();
                  nextPreviews.sizeChart = preview;
                }
              } catch {
                /* ignore preview errors */
              }
            }));
            setRefChangePreviews(nextPreviews);
            const attrCount = nextPreviews.productAttributes?.count;
            const sizeCount = nextPreviews.sizeChart?.count;
            const detailBits = [];
            if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
            if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
            showToast(
              detailBits.length
                ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
                : conflictToastMessage(blocking),
              'warning',
              3000,
            );
            const firstType = blocking[0]?.type;
            if (firstType === TEMPLATE_VERSION_TYPES.sizeChart) {
              setActiveSection('size');
            } else {
              setActiveSection('attrs');
            }
            const firstField = conflictFieldKey(firstType) || 'productAttributes';
            const el = document.querySelector(`[data-field-error="${firstField}"]`);
            if (el?.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        setRefVersionConflicts({});
        setRefChangePreviews({});
      }

      // 有类目时须等属性模板就绪，再按当前类目剔除旧残留属性后落库
      if (form.productTypeId && (attrLoading || !attrTpl)) {
        showToast(t('templateEditor.attrsNotReady'), 'warning');
        setActiveSection('attrs');
        return;
      }

      if (sizeChartClassError) {
        showToast(sizeChartClassError, 'error');
        setFieldErrors((prev) => ({ ...prev, sizeChart: sizeChartClassError }));
        setActiveSection('spec');
        const el = document.querySelector('[data-field-error="sizeChart"]');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (sizeChartRequired && (officialMode || shopId)) {
        const sizeCheck = officialSizeChartRef.current?.validate?.();
        if (sizeCheck && !sizeCheck.ok) {
          showToast(sizeCheck.message || t('templateEditor.fillSizeChart'), 'error');
          setFieldErrors((prev) => ({ ...prev, sizeChart: sizeCheck.message || t('templateEditor.fillSizeChart') }));
          setActiveSection('spec');
          const el = document.querySelector('[data-field-error="sizeChart"]');
          if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }

      if (modelRequired) {
        const goodsModels = Array.isArray(form.goodsModels) ? form.goodsModels : [];
        if (!goodsModels.length) {
          showToast(t('templateEditor.modelRequired'), 'error');
          setFieldErrors((prev) => ({ ...prev, goodsModels: t('templateEditor.modelRequired') }));
          setActiveSection('spec');
          const el = document.querySelector('[data-field-error="goodsModels"]');
          if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }

      if (vehicleLibraryRequired && !hasTemuVehicleLibraryData(form)) {
        showToast(t('templateEditor.vehicleRequired'), 'error');
        setFieldErrors((prev) => ({
          ...prev,
          goodsVehiclePropertyRelation: t('templateEditor.vehicleRequired'),
        }));
        setActiveSection('attrs');
        const el = document.querySelector('[data-field-error="goodsVehiclePropertyRelation"]');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (semiSkuNetContentRequired) {
        const skcList = Array.isArray(form.skcList) ? form.skcList : [];
        for (const skc of skcList) {
          for (const sku of (skc?.skuRows || [])) {
            // 只认净含量字段；货品净重 netWeight 不能顶替
            const raw = String(sku?.netContentNumber ?? sku?.netContent ?? '').trim();
            const unit = Number(sku?.netContentUnitCode || 0);
            const n = Number(raw);
            if (!raw || !Number.isFinite(n) || n <= 0 || !Number.isInteger(n) || ![1, 2, 3, 4, 5, 6, 7, 8].includes(unit)) {
              showToast(t('templateEditor.netContentRequired'), 'error');
              setFieldErrors((prev) => ({ ...prev, supply: t('templateEditor.netContentRequired') }));
              setActiveSection('size');
              return;
            }
          }
        }
      }

      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      const formWithPrint = officialMode
        ? form
        : await enrichTemuFormPrintNames(form);
      const cleanedAttributes = attrTpl
        ? filterProductAttributesToCategory(formWithPrint.productAttributes, attrTpl)
        : (formWithPrint.productAttributes || {});
      const formToPersist = {
        ...formWithPrint,
        productAttributes: cleanedAttributes,
      };
      if (formWithPrint !== form || cleanedAttributes !== formWithPrint.productAttributes) {
        setForm(formToPersist);
      }

      const body = {
        styleNo,
        name,
        remark: String(templateMeta.remark || '').trim() || undefined,
        productTypeId: formToPersist.productTypeId,
        categoryId: formToPersist.categoryId,
        categoryPathLabel: formToPersist.categoryPathLabel || undefined,
        autoRuleEnabled: !!templateMeta.autoRuleEnabled,
        sortOrder: Number.isFinite(Number(templateMeta.sortOrder)) ? Number(templateMeta.sortOrder) : 0,
        payload: {
          // 落库前剥掉 blob:/data:，只留 canonical /uploads/…，隔日打开才不会灰块
          form: persistTemuFormImageSlots({
            ...buildPublishFormSnapshot(formToPersist),
            selectedAttrTemplateId: nextAttrTemplateId,
            selectedSizeChartTemplateId: nextSizeChartTemplateId,
          }),
          selectedAttrTemplateId: nextAttrTemplateId,
          selectedSizeChartTemplateId: nextSizeChartTemplateId,
          selectedAttrTemplateVersion: nextAttrTemplateVersion,
          selectedSizeChartTemplateVersion: nextSizeChartTemplateVersion,
        },
      };
      if (officialMode && shopType) {
        body.shopType = shopType;
      }
      if (templateId && templateVersion != null) {
        body.version = templateVersion;
      }
      if (!templateId && !officialMode) {
        body.scope = templateScope;
      }
      const res = officialMode
        ? (templateId
          ? await adminAPI.updateOfficialTemuProductTemplate(templateId, body)
          : await adminAPI.createOfficialTemuProductTemplate(body))
        : (templateId
          ? await temuPlatformAPI.updateProductTemplate(shopId, templateId, body)
          : await temuPlatformAPI.createProductTemplate(shopId, body));
      const savedItem = officialMode
        ? (res?.item || res?.data?.item || res)
        : (res?.item || res);
      if (savedItem?.id) setTemplateId(String(savedItem.id));
      if (savedItem && parseTemplateVersion(savedItem.version) != null) {
        setTemplateVersion(parseTemplateVersion(savedItem.version));
      }
      setVersionConflict(false);
      setTemplateMeta((prev) => ({ ...prev, styleNo }));
      showToast(templateId ? fixedUi('已保存到产品模板') : fixedUi('已创建产品模板'), 'success');
      navigate(officialMode && afterSaveNavigate ? afterSaveNavigate : buildTemuPath('templates/product'));
    } catch (e) {
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'product' }]), 'warning', 3000);
        setVersionConflict(true);
        setActiveSection('basic');
      } else {
        showToast(e?.message || fixedUi('保存产品模板失败'), 'error');
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const onPublishFromTemplate = async () => {
    if (!shopId) {
      showToast(t('templateEditor.pickShopPublish'), 'warning');
      return;
    }
    try {
      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      const formWithPrint = await enrichTemuFormPrintNames(form);
      if (formWithPrint !== form) {
        setForm(formWithPrint);
      }
      const token = await writeProductTemplatePublishHandoff(
        persistTemuFormImageSlots(buildPublishFormSnapshot(formWithPrint)),
      );
      if (!token) {
        if (templateId) {
          showToast(t('templateEditor.passFail'), 'warning');
          openTemuPathInNewTab(
            `products/publish?productTemplateId=${encodeURIComponent(templateId)}`,
          );
          return;
        }
        showToast(t('templateEditor.passNeedSave'), 'error');
        return;
      }
      openTemuPathInNewTab(
        `products/publish?fromProductTemplate=${encodeURIComponent(token)}`,
      );
    } catch (e) {
      showToast(e?.message || fixedUi('打开发品页失败'), 'error');
    }
  };

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, null),
    [attrTpl],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );
  const visibleRequiredAttrs = useMemo(
    () => requiredAttrs.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [requiredAttrs, allProductAttrs, form.productAttributes],
  );
  const visibleOptionalAttrs = useMemo(
    () => optionalAttrs.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [optionalAttrs, allProductAttrs, form.productAttributes],
  );
  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const syncTitleEnFromZh = async (zhTitle, { silent = false, force = false } = {}) => {
    const zh = String(zhTitle || '').trim();
    if (!zh) {
      setForm((prev) => (prev.titleEn ? { ...prev, titleEn: '' } : prev));
      lastAutoTitleEnRef.current = '';
      return '';
    }
    const seq = ++titleTranslateSeqRef.current;
    setTitleTranslateBusy(true);
    try {
      const titleEn = await translateTemuTitleToEnglish(zh);
      if (seq !== titleTranslateSeqRef.current) return titleEn;
      setForm((prev) => {
        const currentEn = String(prev.titleEn || '').trim();
        const lastAuto = String(lastAutoTitleEnRef.current || '').trim();
        if (!force && currentEn && currentEn !== lastAuto) {
          return prev;
        }
        lastAutoTitleEnRef.current = titleEn;
        return { ...prev, titleEn };
      });
      return titleEn;
    } catch (error) {
      if (seq !== titleTranslateSeqRef.current) return '';
      if (!silent) {
        showToast(error?.message || fixedUi('英文名称自动翻译失败'), 'warning');
      }
      throw error;
    } finally {
      if (seq === titleTranslateSeqRef.current) {
        setTitleTranslateBusy(false);
      }
    }
  };

  useEffect(() => {
    const zh = String(form.title || '').trim();
    if (!zh) {
      if (form.titleEn) {
        setForm((prev) => ({ ...prev, titleEn: '' }));
      }
      lastAutoTitleEnRef.current = '';
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void syncTitleEnFromZh(zh, { silent: true, force: false }).catch(() => { });
    }, 700);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const handleAiTitle = async () => {
    if (aiTitleBusy || savingTemplate) return;
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiTitleDialog'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    setAiTitleBusy(true);
    showToast(t('templateEditor.aiTitleBusyTemu'), 'info');
    try {
      const title = await generateTemuPublishAiTitle({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        maxChars: titleMaxWeight,
      });
      const zh = clipTemuProductNameToLimit(title, titleMaxWeight);
      titleTranslateSeqRef.current += 1;
      setForm((prev) => ({
        ...prev,
        title: zh,
      }));
      showToast(t('templateEditor.translatingEnToast'), 'info');
      try {
        await syncTitleEnFromZh(zh, { silent: true, force: true });
        showToast(t('templateEditor.aiTitleDoneTemu'), 'success');
      } catch (translateError) {
        showToast(translateError?.message || t('templateEditor.aiTitlePartial'), 'warning');
      }
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiTitleFail'), 'error');
    } finally {
      setAiTitleBusy(false);
    }
  };

  const resolvedAttrStyleNo = useMemo(
    () => styleNoFromSupplierCode(
      templateMeta.styleNo || form.supplierCode,
      attrTemplates.map((item) => item?.styleNo).filter(Boolean),
    ),
    [templateMeta.styleNo, form.supplierCode, attrTemplates],
  );

  useEffect(() => {
    const target = String(form.productTypeId || form.categoryId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    fetchAttrTemplateDetail(officialMode, shopId, picked.id, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || {};
        const payload = item.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    officialMode,
    form.productTypeId,
    form.categoryId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    templateScope,
    shopTemplateQuery,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if ((!officialMode && !shopId) || !id) return;
    if (!allowedAttrIds.length) {
      showToast(fixedUi("当前类目属性尚未加载完成"), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
      const item = res?.item || {};
      const tplCatId = String(item.categoryId || item.payload?.categoryId || '').trim();
      const tplTypeId = String(item.productTypeId || item.payload?.productTypeId || '').trim();
      const curCatId = String(form.categoryId || '').trim();
      const curTypeId = String(form.productTypeId || '').trim();
      // 属性库只能给「当前叶子类目 schema」填值；跨类目模板禁止套用，避免男装字段出现在童装上
      if (tplCatId && curCatId && tplCatId !== curCatId) {
        showToast(
          fixedUiTpl('该属性库属于类目 {0}，与当前类目 {1} 不一致，已取消套用。请选用当前类目下的属性库', tplCatId, curCatId),
          'error',
        );
        setSelectedAttrTemplateId('');
        return;
      }
      if (!tplCatId && tplTypeId && curTypeId && tplTypeId !== curTypeId) {
        showToast(
          fixedUi('该属性库商品类型与当前类目不一致，已取消套用'),
          'error',
        );
        setSelectedAttrTemplateId('');
        return;
      }
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性；仍按当前平台 schema 对齐，不会新增 schema 外字段
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi('套用属性库失败'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!officialMode && !shopId) {
      showToast(fixedUi("请先选择店铺"), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('templateEditor.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(fixedUi("请先填写类目属性再保存到属性库"), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!form.productTypeId || (!officialMode && !shopId)) return;
    try {
      const listRes = await fetchAttrTemplateList(officialMode, shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
        ...(officialMode ? { shopType } : { scope: templateScope }),
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };

  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return visibleOptionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    )));
    const head = visibleOptionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = visibleOptionalAttrs
      .slice(9)
      .filter((a) => hintIds.has(String(a.attributeId)))
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    visibleOptionalAttrs,
    refChangePreviews.productAttributes,
    refVersionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    ),
    [refChangePreviews.productAttributes, refVersionConflicts.productAttributes],
  );

  if (!officialMode && !temuShopCtx) {
    return (
      <div className="tpo-wizard tpo-publish">
        <p className="tpo-muted">{t('templateEditor.openTemu')}</p>
      </div>
    );
  }

  return (
    <div className={`tpo-wizard tpo-publish${savingTemplate ? ' is-publishing' : ''}`}>
      {savingTemplate ? (
        <div className="tpo-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="tpo-publish-loading__card">
            <i className="tpo-publish-loading__spinner" aria-hidden />
            <strong>{t('templateEditor.saving')}</strong>
            <span>{t('templateEditor.savingHint')}</span>
          </div>
        </div>
      ) : null}
      <aside className="tpo-wizard__steps" aria-label={t('templateEditor.sectionsAria')}>
        <div className="tpo-wizard__steps-label">
          {t('productTemplate.filePrefix')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`tpo-wizard__step${activeSection === item.id ? ' tpo-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="tpo-wizard__step-index">{idx + 1}</span>
            <span className="tpo-wizard__step-title">{t(SECTION_TITLE_KEYS[item.id], { defaultValue: item.title })}</span>
          </button>
        ))}
        <div className="tpo-publish__progress">
          <div className="tpo-publish__progress-label">{t('tiktokPublish.progress', { percent: completion })}</div>
          <div className="tpo-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="tpo-wizard__panel">
        <div className="tpo-wizard__panel-scroll">
          {templateLoading ? <p className="tpo-wizard__inline-hint">{t('templateEditor.loading')}</p> : null}
          {fieldErrors.form ? (
            <div className="tpo-publish-form-error" role="alert" data-field-error="form">
              {fieldErrors.form}
            </div>
          ) : null}

          {/* —— 基础信息 —— */}
          <div id="tpo-pub-basic" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
            <h3>{t('publishChrome.sectionBasic')}</h3>

            <div className="tpo-wizard__form">
              {officialMode ? (
                <div className="tpo-wizard__form tpo-wizard__form--3 tpo-field--full">
                  <label className="tpo-field">
                    <span className="tpo-field__label">
                      {t('shop.shopType')} <RequiredMark />
                    </span>
                    <select
                      className="tpo-select"
                      aria-label={t('shop.shopType')}
                      value={shopType || ''}
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => handleOfficialShopTypeChange(e.target.value)}
                    >
                      <option value="" disabled>
                        {t('shop.needShopType')}
                      </option>
                      {TEMU_SHOP_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={`tpo-field${versionConflict ? ' tpo-field--version-conflict' : ''}`}>
                    <span className="tpo-field__label">
                      {t('productTemplate.colName')} <RequiredMark />
                      <TemplateVersionConflictMark
                        active={versionConflict}
                        label={t('photoLib.updatedMark')}
                        reloading={reloadingVersion}
                        onReload={reloadTemplateFromServer}
                      />
                    </span>
                    <input
                      className="tpo-input"
                      value={templateMeta.name}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.namePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </label>
                  <label className="tpo-field">
                    <span className="tpo-field__label">
                      {t('productTemplate.colStyleNo')} <RequiredMark />
                    </span>
                    <input
                      className="tpo-input"
                      value={templateMeta.styleNo}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.stylePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, styleNo: e.target.value }))}
                    />
                  </label>
                </div>
              ) : (
                <>
                  <label className={`tpo-field tpo-field--full${versionConflict ? ' tpo-field--version-conflict' : ''}`}>
                    <span className="tpo-field__label">
                      {t('productTemplate.colName')} <RequiredMark />
                      <TemplateVersionConflictMark
                        active={versionConflict}
                        label={t('photoLib.updatedMark')}
                        reloading={reloadingVersion}
                        onReload={reloadTemplateFromServer}
                      />
                    </span>
                    <input
                      className="tpo-input"
                      value={templateMeta.name}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.namePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </label>
                  <label className="tpo-field tpo-field--full">
                    <span className="tpo-field__label">
                      {t('productTemplate.colStyleNo')} <RequiredMark />
                    </span>
                    <input
                      className="tpo-input"
                      value={templateMeta.styleNo}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.stylePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, styleNo: e.target.value }))}
                    />
                  </label>
                </>
              )}
              <label className="tpo-field tpo-field--full">
                <span className="tpo-field__label">{t('productTemplate.colRemark')}</span>
                <input
                  className="tpo-input"
                  value={templateMeta.remark}
                  disabled={templateLoading || savingTemplate}
                  maxLength={200}
                  placeholder={t('templateEditor.remarkPh')}
                  onChange={(e) => setTemplateMeta((prev) => ({ ...prev, remark: e.target.value }))}
                />
              </label>
              <div className="tpo-field tpo-field--full tpo-template-auto-rule">
                <div className="tpo-template-auto-rule__switch">
                  <TemuSwitch
                    checked={!!templateMeta.autoRuleEnabled}
                    disabled={templateLoading || savingTemplate}
                    ariaLabel={t('productTemplate.colAutoRule')}
                    onChange={(next) => setTemplateMeta((prev) => ({ ...prev, autoRuleEnabled: next }))}
                  />
                  <span>{t('productTemplate.colAutoRule')}</span>
                </div>
                <p className="tpo-wizard__inline-hint">
                  {t('templateEditor.autoHint')}
                </p>
                <label className="tpo-field">
                  <span className="tpo-field__label">{t('productTemplate.colPriority')}</span>
                  <input
                    className="tpo-input"
                    type="number"
                    step={1}
                    value={templateMeta.sortOrder}
                    disabled={templateLoading || savingTemplate}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setTemplateMeta((prev) => ({
                        ...prev,
                        sortOrder: Number.isFinite(next) ? next : 0,
                      }));
                    }}
                  />
                </label>
              </div>

              <label className={`tpo-field tpo-field--full${fieldErrors.categoryId ? ' tpo-field--invalid' : ''}`} data-field-error="categoryId">
                <span className="tpo-field__label">
                  {t('publishBasic.category')}
                  {!officialMode ? <RequiredMark /> : null}
                </span>
                <CategoryCascaderSelect
                  shopId={officialMode ? undefined : shopId}
                  officialShopType={officialMode ? shopType : undefined}
                  categories={meta.categories}
                  value={form.categoryId}
                  label={form.categoryPathLabel}
                  disabled={(!officialMode && !shopId) || metaLoading || templateLoading || savingTemplate}
                  placeholder={
                    officialMode
                      ? (metaLoading ? t('templateEditor.loadingCats') : t('publishBasic.pickCategory'))
                      : (!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory'))
                  }
                  onChange={onCategoryChange}
                />

                <FieldErrorText message={fieldErrors.categoryId} />
              </label>

              <label className={`tpo-field tpo-field--full${fieldErrors.title ? ' tpo-field--invalid' : ''}`} data-field-error="title">
                <span className="tpo-field__label tpo-field__label-row">
                  <span>
                    {t('publishBasic.productName')}
                    {!officialMode ? <RequiredMark /> : null}
                  </span>
                  <button
                    type="button"
                    className="tpo-btn tpo-btn--danger tpo-btn--sm"
                    disabled={aiTitleBusy || templateLoading || savingTemplate || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiTitle(); }}
                  >
                    {aiTitleBusy ? t('publishBasic.generating') : t('publishBasic.aiTitle')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiTitleBusy && !titleTranslateBusy}
                  label={t('publishBasic.aiTitleLoadingTemu')}
                >
                  <textarea
                    className="tpo-textarea tpo-textarea--title"
                    value={form.title}
                    rows={2}
                    readOnly={aiTitleBusy && !titleTranslateBusy}
                    onChange={(e) => update('title', clipTemuProductNameToLimit(e.target.value, titleMaxWeight))}
                    placeholder={t('publishBasic.titlePhTemu')}
                  />
                </TitleFieldLoadingShell>
                <span className="tpo-publish__counter" title={t('publishBasic.weightTitle')}>
                  {titleWeight}/{titleMaxWeight}
                </span>
                <FieldErrorText message={fieldErrors.title} />
              </label>

              <label className={`tpo-field tpo-field--full${fieldErrors.titleEn ? ' tpo-field--invalid' : ''}`} data-field-error="titleEn">
                <span className="tpo-field__label">
                  {t('publishBasic.titleEn')}
                </span>
                <TitleFieldLoadingShell
                  loading={titleTranslateBusy || aiTitleBusy}
                  label={titleTranslateBusy ? t('publishBasic.translateEn') : t('publishBasic.waitTitle')}
                >
                  <textarea
                    className="tpo-textarea tpo-textarea--title"
                    value={form.titleEn}
                    rows={2}
                    readOnly={titleTranslateBusy || aiTitleBusy}
                    onChange={(e) => update('titleEn', clipTemuProductNameToLimit(e.target.value, titleMaxWeight))}
                    placeholder={t('publishBasic.titleEnPh')}
                  />
                </TitleFieldLoadingShell>
                <span className="tpo-publish__counter" title={t('publishBasic.weightTitle')}>
                  {titleEnWeight}/{titleMaxWeight}
                </span>
                <FieldErrorText message={fieldErrors.titleEn} />
              </label>

              <label className={`tpo-field${fieldErrors.originRegion ? ' tpo-field--invalid' : ''}`} data-field-error="originRegion">
                <span className="tpo-field__label">
                  {t('publishBasic.origin')}
                  {!officialMode ? <RequiredMark /> : null}
                </span>
                <SearchableSelect
                  value={form.originRegion || 'CN'}
                  placeholder={t('publishBasic.pickOrigin')}
                  allowClear={false}
                  onChange={(v) => {
                    const nextOrigin = normalizeTemuOriginRegion(v) || 'CN';
                    setForm((prev) => ({
                      ...prev,
                      originRegion: nextOrigin,
                      originCountry: nextOrigin,
                      originRegion2Id: nextOrigin === 'CN' ? prev.originRegion2Id : '',
                    }));
                  }}
                  options={localizeRegionOptions(TEMU_REGION1_OPTIONS)}
                />
                <FieldErrorText message={fieldErrors.originRegion} />
              </label>
              {String(normalizeTemuOriginRegion(form.originRegion) || 'CN') === 'CN' ? (
                <label className={`tpo-field${fieldErrors.originRegion2Id ? ' tpo-field--invalid' : ''}`} data-field-error="originRegion2Id">
                  <span className="tpo-field__label">
                    {t('publishBasic.originProvince')}
                    {!officialMode ? <RequiredMark /> : null}
                  </span>
                  <SearchableSelect
                    value={form.originRegion2Id}
                    placeholder={t('publishBasic.pickProvince')}
                    onChange={(v) => update('originRegion2Id', v)}
                    options={localizeCnProvinceOptions(CN_REGION2_OPTIONS)}
                  />
                  <FieldErrorText message={fieldErrors.originRegion2Id} />
                </label>
              ) : (
                <div className={`tpo-field tpo-field--full${fieldErrors.originCertFiles ? ' tpo-field--invalid' : ''}`} data-field-error="originCertFiles">
                  <span className="tpo-field__label">
                    {t('publishBasic.originCert')}
                    {isFullManaged ? <RequiredMark /> : null}
                  </span>
                  <p className="tpo-wizard__inline-hint">
                    {t('publishBasic.originCertHint')}
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    disabled={originCertBusy || !shopId}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file || !shopId) return;
                      setOriginCertBusy(true);
                      try {
                        const res = await temuPlatformAPI.uploadPublishGoodsFile(shopId, file);
                        const url = String(res?.url || res?.fileUrl || '').trim();
                        if (!url) throw new Error(t('publishBasic.uploadNoUrl'));
                        update('originCertFiles', [{
                          fileName: String(res?.fileName || file.name),
                          fileUrl: url,
                        }]);
                        showToast(t('publishBasic.originUploaded'), 'success');
                      } catch (err) {
                        showToast(err?.message || t('publishBasic.originUploadFail'), 'error');
                      } finally {
                        setOriginCertBusy(false);
                      }
                    }}
                  />
                  {(form.originCertFiles || []).map((f, idx) => (
                    <div key={`${f.fileUrl}-${idx}`} className="tpo-guide-file__list" style={{ marginTop: 8 }}>
                      <span>{f.fileName || f.fileUrl}</span>
                      {' '}
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                        onClick={() => update('originCertFiles', [])}
                      >
                        {t('shop.delete')}
                      </button>
                    </div>
                  ))}
                  <FieldErrorText message={fieldErrors.originCertFiles} />
                </div>
              )}
              <div className={`tpo-field tpo-field--full${fieldErrors.customManufacturingLocations ? ' tpo-field--invalid' : ''}`} data-field-error="customManufacturingLocations">
                <span className="tpo-field__label">
                  {t('publishBasic.mfg')}
                  {isFullManaged ? <RequiredMark /> : null}
                </span>
                <SearchableMultiSelect
                  value={(form.customManufacturingLocations || []).map(String)}
                  onChange={(next) => update(
                    'customManufacturingLocations',
                    (Array.isArray(next) ? next : [])
                      .map((item) => resolveTemuRegion1ShortName(item))
                      .filter(Boolean),
                  )}
                  placeholder={isFullManaged ? t('publishBasic.mfgPh') : t('publishBasic.mfgOptional')}
                  options={localizeRegionOptions(TEMU_REGION1_OPTIONS)}
                />

                <FieldErrorText message={fieldErrors.customManufacturingLocations} />
              </div>
            </div>
          </div>

          {/* —— 类目属性 —— */}
          <div
            id="tpo-pub-attrs"
            className="tpo-wizard__block"
            onFocusCapture={() => setActiveSection('attrs')}
            data-field-error="productAttributes"
          >
            <div className="tpo-publish__block-head">
              <h3>{t('publishChrome.sectionAttrs')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.productAttributes} />
            {!form.categoryId ? (
              <p className="tpo-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
            ) : attrLoading ? (
              <div className="tpo-attr-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="tpo-attr-loading__banner">
                  <span className="tpo-attr-loading__spinner" aria-hidden />
                  <div className="tpo-attr-loading__copy">
                    <strong>{t('publishAttr.loadingTitle')}</strong>
                    <span>{t('publishAttr.loadingHint')}</span>
                  </div>
                </div>
                <div className="tpo-wizard__form tpo-wizard__form--attrs tpo-attr-loading__skeleton">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="tpo-attr-loading__card">
                      <span className="tpo-attr-loading__line tpo-attr-loading__line--label" />
                      <span className="tpo-attr-loading__line tpo-attr-loading__line--field" />
                    </div>
                  ))}
                </div>
              </div>
            ) : attrError && !(visibleRequiredAttrs.length || visibleOptionalAttrs.length) ? (
              <p className="tpo-wizard__inline-error" role="alert">{attrError}</p>
            ) : (
              <>
                {attrError ? <p className="tpo-wizard__inline-hint">{attrError}</p> : null}
                {allProductAttrs.length > 0 ? (
                  <div className={`tpo-size-chart-template-bar tpo-attr-template-bar${refVersionConflicts.productAttributes ? ' tpo-attr-template-bar--version-conflict' : ''}`}>
                    <TemplateVersionConflictMark
                      active={!!refVersionConflicts.productAttributes}
                      label={
                        refChangePreviews.productAttributes?.count != null
                          ? t('publishAttr.updatedCount', { count: refChangePreviews.productAttributes.count })
                          : t('photoLib.updatedMark')
                      }
                      reloading={reloadingRefType === TEMPLATE_VERSION_TYPES.attribute}
                      resolveChangePreview={async () => (
                        refChangePreviews.productAttributes
                        || buildAttrChangePreview()
                      )}
                      onReload={() => reloadReferencedTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                    />
                    <select
                      className="tpo-select"
                      value={selectedAttrTemplateId}
                      disabled={attrTemplateBusy || (!officialMode && !shopId)}
                      onChange={(e) => onAttrTemplateSelect(e.target.value)}
                    >
                      <option value="">{t('publishAttr.pickLib')}</option>
                      {attrTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tpo-btn tpo-btn--primary tpo-btn--sm"
                      disabled={attrTemplateBusy || (!officialMode && !shopId) || !form.categoryId}
                      onClick={openAttrSaveModal}
                    >
                      {t('shop.save')}
                    </button>
                  </div>
                ) : null}
                {visibleRequiredAttrs.length > 0 ? (
                  <>
                    <h4 className="tpo-publish__subhead">{t('publishAttr.requiredAttrs')}</h4>
                    <div className="tpo-wizard__form tpo-wizard__form--attrs">
                      {layoutAttributeFields(visibleRequiredAttrs).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={isEditorAttrRequiredMark(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                          allowedValueIds={editorAttrAllowedValueIds(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {visibleOptionalAttrs.length > 0 ? (
                  <>
                    <h4 className="tpo-publish__subhead">{t('publishAttr.optionalAttrs')}</h4>
                    <div className="tpo-wizard__form tpo-wizard__form--attrs">
                      {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                          allowedValueIds={editorAttrAllowedValueIds(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                        />
                      ))}
                    </div>
                    {visibleOptionalAttrs.length > 9 ? (
                      <div className="tpo-publish__expand">
                        <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                          {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: visibleOptionalAttrs.length - 9 })}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!visibleRequiredAttrs.length && !visibleOptionalAttrs.length ? (
                  <p className="tpo-wizard__inline-hint">{t('publishAttr.none')}</p>
                ) : null}
              </>
            )}
          </div>

          {/* —— 规格供应 —— */}
          <div id="tpo-pub-spec" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
            <h3>{t('publishChrome.sectionSpecTitle')}</h3>
            <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
            <SpecSupplySection
              colorAttr={colorAttr}
              sizeAttr={sizeAttr}
              skcList={form.skcList}
              selectedSizeIds={form.selectedSizeIds || []}
              shopId={shopId}
              skipTemuConvert={officialMode}
              categoryId={form.categoryId}
              customAttrPerm={customAttrPerm}
              onCustomValueAdded={appendCustomAttrValue}
              currency={platformCurrency}
              currencyPending={publishCurrencyLoading || !platformCurrency}
              priceMode={priceMode}
              attrLoading={attrLoading}
              defaultSupplierSkuSpu=""
              printImages={form.printImages || []}
              onPrintImagesChange={updatePrintImages}
              materialImage={form.materialImage}
              materialImgError={fieldErrors.materialImgUrl}
              onMaterialImagesChange={onMaterialImagesChange}
              temuVideos={form.temuVideos || []}
              videoError={fieldErrors.temuVideos}
              onVideosChange={onVideosChange}
              onColorsChange={onColorsChange}
              onSizesChange={onSizesChange}
              onUpdateSkc={updateSkc}
              onUpdateSku={updateSku}
              onRemoveSku={removeSku}
              onBatchUpdateSku={batchUpdateSku}
              afterMaterialContent={(modelRequired || sizeChartRequired || sizeChartClassLoading || sizeChartClassError) ? (
                <>
                  <TemuModelSelectBar
                    shopId={shopId}
                    officialMode={officialMode}
                    shopType={shopType}
                    visible
                    required={modelRequired}
                    modelType={publishModelType}
                    value={form.goodsModels || []}
                    onChange={(next) => update('goodsModels', next)}
                    errorMessage={fieldErrors.goodsModels}
                    sizeOptions={modelTryOnSizeOptions}
                  />
                  {(sizeChartRequired && sizeChartClass?.classId && !sizeChartClassLoading) ? (
                    <TemuOfficialSizeChartPanel
                      key={`official-size-${officialMode ? shopType : shopId}-${form.categoryId}-${sizeChartClass?.classId || ''}-${templateId || 'new'}`}
                      ref={officialSizeChartRef}
                      shopId={officialMode ? '' : shopId}
                      officialMode={officialMode}
                      shopType={shopType}
                      categoryId={form.categoryId}
                      productTypeId={form.productTypeId}
                      categoryPathLabel={form.categoryPathLabel}
                      selectedSizeIds={form.selectedSizeIds}
                      selectedSizeLabels={selectedSizeLabels}
                      defaultStyleNo={templateMeta.styleNo}
                      required={sizeChartRequired}
                      sizeClass={sizeChartClass}
                      loading={sizeChartClassLoading}
                      errorMessage={fieldErrors.sizeChart}
                      initialDraft={form.officialSizeChart}
                      onDraftChange={onOfficialSizeChartDraftChange}
                    />
                  ) : null}
                  {sizeChartClassError ? (
                    <p className="tpo-wizard__inline-error" role="alert">{sizeChartClassError}</p>
                  ) : null}
                  {sizeChartClassLoading ? (
                    <p className="tpo-wizard__hint" aria-live="polite">{fixedUi("正在加载尺码表规则…")}</p>
                  ) : null}
                </>
              ) : null}
            />
          </div>

          <div className="tpo-wizard__block" onFocusCapture={() => setActiveSection('spec')}>
            <TemuGuideFilePanel
              shopId={officialMode ? '' : shopId}
              needGuideFile={needGuideFile}
              needGuideLoading={needGuideLoading}
              files={form.guideFiles || []}
              onChange={(next) => update('guideFiles', next)}
              errorMessage={fieldErrors.guideFiles}
            />
          </div>

          {vehicleLibraryScopeActive ? (
            <div className="tpo-wizard__block" onFocusCapture={() => setActiveSection('attrs')}>
              <TemuVehicleFitmentPanel
                visible
                required={vehicleLibraryRequired}
                attrTpl={attrTpl}
                productAttributes={form.productAttributes}
                value={form.goodsVehiclePropertyRelation}
                onChange={(next) => update('goodsVehiclePropertyRelation', next)}
                errorMessage={fieldErrors.goodsVehiclePropertyRelation}
              />
            </div>
          ) : null}

          {/* —— 包装信息 —— */}
          <div
            id="tpo-pub-size"
            className="tpo-wizard__block"
            onFocusCapture={() => setActiveSection('size')}
          >
            <h3>{t('publishChrome.sectionSize')}</h3>

            <p className="tpo-wizard__inline-hint">
              {accessoryLoading ? t('publishPkg.loadingAcc') : null}
            </p>
            <div className="tpo-publish__table-wrap tpo-publish__table-wrap--scroll">
              <table className="tpo-publish__table tpo-pkg-table">
                <colgroup>
                  <col className="tpo-pkg-col tpo-pkg-col--color" />
                  <col className="tpo-pkg-col tpo-pkg-col--size" />
                  <col className="tpo-pkg-col tpo-pkg-col--sensitive" />
                  <col className="tpo-pkg-col tpo-pkg-col--dims" />
                  <col className="tpo-pkg-col tpo-pkg-col--weight" />
                  <col className="tpo-pkg-col tpo-pkg-col--net-weight" />
                  {semiSkuNetContentRequired ? (
                    <col className="tpo-pkg-col tpo-pkg-col--net-content" />
                  ) : null}
                  <col className="tpo-pkg-col tpo-pkg-col--sku-class" />
                  <col className="tpo-pkg-col tpo-pkg-col--accessories" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{colorLabel}</th>
                    <th>{sizeLabel}</th>
                    <th className="tpo-pkg-table__sensitive">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.sensitive')} <RequiredMark />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('sensitive')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__dims">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.volume', { unit: TEMU_DIM_UNIT })} <RequiredMark />
                          <FieldHelpMark text={t('publishPkg.volumeHelpTemu')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('dims')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__weight">
                      <span className="tpo-supply-th">
                        <span>{t('publishPkg.weight')} <RequiredMark /></span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('weight')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__net-weight">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.goodsNet')}
                          {netWeightRequired ? <RequiredMark /> : null}
                          <FieldHelpMark text={netWeightRequired
                            ? t('publishPkg.netReqHelp')
                            : t('publishPkg.netOptHelp')}
                          />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('weight')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    {semiSkuNetContentRequired ? (
                      <th className="tpo-pkg-table__net-content">
                        <span className="tpo-supply-th">
                          <span className="tpo-th-with-help">
                            {t('publishPkg.netContent')}
                            <RequiredMark />
                            <FieldHelpMark text={t('publishPkg.netContentHelp')} />
                          </span>
                        </span>
                      </th>
                    ) : null}
                    <th className="tpo-pkg-table__sku-class">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.skuClass')}
                          {semiSkuNetContentRequired ? <RequiredMark /> : null}
                          <FieldHelpMark text={t('publishPkg.skuClassHelpTemu')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('quantity')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__accessories">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.accessories')}
                          <FieldHelpMark text={t('publishPkg.accHelp')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('accessories')}
                          disabled={accessoryLoading || !accessoryOptions.length}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                      {accessoryLoadError ? (
                        <div className="tpo-wizard__inline-hint" role="status">
                          {accessoryLoading ? t('publishPkg.loadingAcc') : accessoryLoadError}
                          {!accessoryLoading && accessoryLoadError ? (
                            <button
                              type="button"
                              className="tpo-supply-th__batch"
                              style={{ marginLeft: 8 }}
                              onClick={() => setAccessoryReloadKey((k) => k + 1)}
                            >
                              {t('commerce.retry')}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {form.skcList.filter((skc) => skcHasColor(skc)).flatMap((skc) => {
                    const rows = skc.skuRows?.length ? skc.skuRows : [];
                    return rows.map((sku, rowIndex) => {
                      const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                        ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                        : (
                          attributeValueDisplayLabel(
                            sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                            || sku.sizeLabel,
                          ) || '—'
                        );
                      const skuClass = String(sku.skuClassification || '1');
                      const isMulti = skuClass === '2' || skuClass === '3';
                      const sensitiveOn = Number(sku.isSensitive || 0) === 1;
                      const sensitiveSlots = sensitiveSlotsFromList(sku.sensitiveList, 3);
                      const pieceUnit = String(sku.pieceUnitCode || sku.quantityUnit || '1');
                      const pieceCount = sku.numberOfPieces ?? sku.quantity ?? '1';
                      const accessories = Array.isArray(sku.accessories) && sku.accessories.length
                        ? sku.accessories
                        : [createEmptyAccessoryRow()];
                      const filledAccessories = accessories.filter(
                        (row) => Number(row?.vid) > 0 && Number(row?.num) > 0,
                      );
                      const needAccessories = skuClass === '3';
                      return (
                        <tr key={`${skc.id}-${sku.id}-${rowIndex}`}>
                          {rowIndex === 0 ? (
                            <td rowSpan={rows.length} className="tpo-pkg-table__color">
                              {skc.colorLabel || '—'}
                            </td>
                          ) : null}
                          <td className="tpo-pkg-table__size">{sizeName}</td>
                          <td className="tpo-pkg-table__sensitive">
                            <div className="tpo-pkg-sensitive">
                              <SearchableSelect
                                value={String(sku.isSensitive ?? 0)}
                                allowClear={false}
                                onChange={(v) => updateSku(skc.id, sku.id, {
                                  isSensitive: Number(v) === 1 ? 1 : 0,
                                  ...(Number(v) !== 1 ? { sensitiveList: [] } : {}),
                                })}
                                options={localizeOptions(TEMU_SENSITIVE_YES_NO_OPTIONS, YES_NO_KEYS)}
                              />
                              {sensitiveOn ? (
                                <div className="tpo-pkg-sensitive__types">
                                  {sensitiveSlots.map((slotVal, slotIdx) => (
                                    <SearchableSelect
                                      key={`sens-${sku.id}-${slotIdx}`}
                                      value={slotVal}
                                      allowClear
                                      placeholder={t('shop.pleaseSelect')}
                                      onChange={(v) => {
                                        const nextSlots = [...sensitiveSlots];
                                        nextSlots[slotIdx] = v == null ? '' : String(v);
                                        updateSku(skc.id, sku.id, {
                                          sensitiveList: sensitiveListFromSlots(nextSlots),
                                        });
                                      }}
                                      options={localizeOptions(TEMU_SENSITIVE_LIST_OPTIONS, SENSITIVE_KEYS)}
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="tpo-pkg-table__dims">
                            <div className="tpo-pkg-volume">
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.longest')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.length ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.mid')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.width ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.shortest')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.height ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                            </div>
                          </td>
                          <td className="tpo-pkg-table__weight">
                            <div className="tpo-pkg-inline tpo-pkg-inline--weight">
                              <input
                                className="tpo-input tpo-pkg-inline__input"
                                value={sku.weight ?? ''}
                                inputMode="decimal"
                                onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                                placeholder={t('publishPkg.input')}
                              />
                              <span className="tpo-pkg-inline__unit">{TEMU_WEIGHT_UNIT}</span>
                            </div>
                          </td>
                          <td className="tpo-pkg-table__net-weight">
                            <div className="tpo-pkg-inline tpo-pkg-inline--weight">
                              <input
                                className="tpo-input tpo-pkg-inline__input"
                                value={sku.netWeight ?? ''}
                                inputMode="decimal"
                                onChange={(e) => updateSku(skc.id, sku.id, { netWeight: e.target.value })}
                                placeholder={netWeightRequired ? t('publishPkg.input') : t('publishPkg.optionalPh')}
                              />
                              <span className="tpo-pkg-inline__unit">{TEMU_WEIGHT_UNIT}</span>
                            </div>
                          </td>
                          {semiSkuNetContentRequired ? (
                            <td className="tpo-pkg-table__net-content">
                              <div className="tpo-pkg-net-content">
                                <input
                                  className="tpo-input tpo-pkg-inline__input"
                                  value={sku.netContent ?? sku.netContentNumber ?? ''}
                                  inputMode="numeric"
                                  onChange={(e) => updateSku(skc.id, sku.id, { netContent: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <SearchableSelect
                                  value={String(sku.netContentUnitCode || '5')}
                                  allowClear={false}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    netContentUnitCode: String(v || '5'),
                                  })}
                                  options={localizeOptions(TEMU_NET_CONTENT_UNIT_OPTIONS, NET_UNIT_KEYS)}
                                />
                              </div>
                            </td>
                          ) : null}
                          <td className="tpo-pkg-table__sku-class">
                            <div className="tpo-pkg-sku-class">
                              <SearchableSelect
                                value={skuClass}
                                allowClear={false}
                                onChange={(v) => updateSku(skc.id, sku.id, {
                                  skuClassification: String(v || '1'),
                                  ...(String(v || '1') === '1'
                                    ? { individuallyPacked: '', numberOfPieces: pieceCount || '1', quantity: pieceCount || '1' }
                                    : {}),
                                })}
                                options={localizeOptions(TEMU_SKU_CLASSIFICATION_OPTIONS, SKU_CLASS_KEYS)}
                              />
                              <div className="tpo-pkg-sku-class__row">
                                <span>{t('publishPkg.pieceQty')}</span>
                                <input
                                  className="tpo-input tpo-pkg-inline__input--qty"
                                  value={pieceCount}
                                  inputMode="numeric"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    updateSku(skc.id, sku.id, {
                                      numberOfPieces: val,
                                      quantity: val,
                                    });
                                  }}
                                  placeholder={t('publishPkg.input')}
                                />
                                <SearchableSelect
                                  className="tpo-select--unit"
                                  value={pieceUnit}
                                  allowClear={false}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    pieceUnitCode: String(v || '1'),
                                    quantityUnit: String(v || '1'),
                                  })}
                                  options={localizeOptions(TEMU_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS)}
                                />
                              </div>
                              <div className="tpo-pkg-sku-class__row">
                                <span>{t('publishPkg.totalContains')}</span>
                                <input
                                  className="tpo-input tpo-pkg-inline__input--qty"
                                  value={sku.totalContains ?? ''}
                                  inputMode="numeric"
                                  onChange={(e) => updateSku(skc.id, sku.id, {
                                    totalContains: e.target.value,
                                  })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <span className="tpo-pkg-inline__unit">
                                  {optionLabel(TEMU_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS, pieceUnit, t('publishPkg.piece'))}
                                </span>
                              </div>
                              {isMulti ? (
                                <SearchableSelect
                                  value={sku.individuallyPacked === 0 || sku.individuallyPacked === '0'
                                    ? '0'
                                    : (sku.individuallyPacked === 1 || sku.individuallyPacked === '1' ? '1' : '')}
                                  allowClear
                                  placeholder={t('publishPkg.indivPh')}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    individuallyPacked: v == null || v === '' ? '' : String(v),
                                  })}
                                  options={localizeOptions(TEMU_INDIVIDUALLY_PACKED_OPTIONS, PACKED_KEYS)}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="tpo-pkg-table__accessories">
                            <div className="tpo-pkg-accessories">
                              {accessories.map((item, idx) => (
                                <div key={`${sku.id}-acc-${idx}`} className="tpo-pkg-accessories__row">
                                  <SearchableSelect
                                    value={item.vid ? String(item.vid) : ''}
                                    allowClear
                                    placeholder={
                                      accessoryLoading
                                        ? t('publishPkg.accLoading')
                                        : (accessoryOptions.length ? t('publishPkg.accSearch') : t('publishPkg.accNone'))
                                    }
                                    disabled={accessoryLoading || !accessoryOptions.length}
                                    onChange={(v) => {
                                      const opt = accessoryOptions.find((o) => o.value === String(v));
                                      const next = accessories.map((row, i) => (
                                        i === idx
                                          ? {
                                            ...row,
                                            vid: v ? Number(v) : '',
                                            label: opt?.label || '',
                                            unitCode: opt?.unitCode || row.unitCode || 1,
                                            unitName: opt?.unitName || row.unitName || '件',
                                            thumbUrl: opt?.thumbUrl || row.thumbUrl || '',
                                          }
                                          : row
                                      ));
                                      updateSku(skc.id, sku.id, { accessories: next });
                                    }}
                                    options={accessoryOptions}
                                  />
                                  <input
                                    className="tpo-input tpo-pkg-inline__input--qty"
                                    value={item.num ?? ''}
                                    inputMode="numeric"
                                    placeholder={t('publishPkg.qty')}
                                    onChange={(e) => {
                                      const next = accessories.map((row, i) => (
                                        i === idx ? { ...row, num: e.target.value } : row
                                      ));
                                      updateSku(skc.id, sku.id, { accessories: next });
                                    }}
                                  />
                                  <span className="tpo-pkg-inline__unit">
                                    {item.unitName
                                      || accessoryOptions.find((o) => o.value === String(item.vid))?.unitName
                                      || t('publishPkg.piece')}
                                  </span>
                                  <button
                                    type="button"
                                    className="tpo-pkg-accessories__del"
                                    title={t('publishPkg.deleteItem')}
                                    aria-label={t('publishPkg.deleteItem')}
                                    onClick={() => {
                                      const next = accessories.filter((_, i) => i !== idx);
                                      updateSku(skc.id, sku.id, {
                                        accessories: next.length ? next : [createEmptyAccessoryRow()],
                                      });
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                                      <path
                                        fill="currentColor"
                                        d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 13h12a1 1 0 0 0 1-1V7H5v14a1 1 0 0 0 1 1z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="tpo-pkg-accessories__add"
                                disabled={accessories.length >= 20 || accessoryLoading}
                                onClick={() => {
                                  updateSku(skc.id, sku.id, {
                                    accessories: [
                                      ...accessories,
                                      createEmptyAccessoryRow(),
                                    ],
                                  });
                                }}
                              >
                                {t('publishPkg.add')}
                              </button>
                              {needAccessories && !filledAccessories.length ? (
                                <span className="tpo-wizard__inline-hint">{t('publishPkg.mixNeed')}</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>

            <h4 className="tpo-publish__subhead">{t('publishPkg.productPkg')}</h4>

            <div className="tpo-wizard__form tpo-wizard__form--2">
              <label className={`tpo-field${fieldErrors.packageShape ? ' tpo-field--invalid' : ''}`} data-field-error="packageShape">
                <span className="tpo-field__label">{t('publishPkg.outerShape')} <RequiredMark /></span>
                <SearchableSelect
                  value={form.packageShape || ''}
                  allowClear
                  placeholder={t('shop.pleaseSelect')}
                  onChange={(v) => update('packageShape', v == null ? '' : String(v))}
                  options={localizeOptions(TEMU_OUTER_PACKAGE_SHAPE_OPTIONS, OUTER_SHAPE_KEYS)}
                />
                {fieldErrors.packageShape ? (
                  <span className="tpo-field__error" role="alert">{fieldErrors.packageShape}</span>
                ) : null}
              </label>
              <label className={`tpo-field${fieldErrors.packageTypeOuter ? ' tpo-field--invalid' : ''}`} data-field-error="packageTypeOuter">
                <span className="tpo-field__label">{t('publishPkg.outerType')} <RequiredMark /></span>
                <SearchableSelect
                  value={form.packageTypeOuter || ''}
                  allowClear
                  placeholder={t('shop.pleaseSelect')}
                  onChange={(v) => update('packageTypeOuter', v == null ? '' : String(v))}
                  options={localizeOptions(TEMU_OUTER_PACKAGE_TYPE_OPTIONS, OUTER_TYPE_KEYS)}
                />
                {fieldErrors.packageTypeOuter ? (
                  <span className="tpo-field__error" role="alert">{fieldErrors.packageTypeOuter}</span>
                ) : null}
              </label>
            </div>

            <div
              className={`tpo-field tpo-outer-pkg-images${fieldErrors.outerPackageImages ? ' tpo-field--invalid' : ''}`}
              data-field-error="outerPackageImages"
            >
              <span className="tpo-field__label tpo-field__label-with-help">
                {t('publishPkg.outerImages')} <RequiredMark />
                <FieldHelpMark text={t('templateEditor.outerHelpTemuTpl')} />
              </span>
              <p className="tpo-outer-pkg-images__hint">
                {t('publishPkg.outerHint')}
              </p>
              <PublishImageUploadZone
                compact
                labelInside
                label={t('publishPkg.outerImg')}
                imageType={1}
                imageBizType={1}
                imageRule="package"
                multiple
                maxCount={6}
                skipTemuConvert={Boolean(officialMode)}
                value={Array.isArray(form.outerPackageImages) ? form.outerPackageImages : []}
                shopId={shopId}
                onChange={(next) => {
                  update('outerPackageImages', Array.isArray(next) ? next : (next ? [next] : []));
                }}
              />
              {fieldErrors.outerPackageImages ? (
                <span className="tpo-field__error" role="alert">{fieldErrors.outerPackageImages}</span>
              ) : null}
            </div>

            {pkgBatchMode ? (
              <PackageBatchModal
                mode={pkgBatchMode}
                skcList={form.skcList}
                sizeAttr={sizeAttr}
                accessoryOptions={accessoryOptions}
                weightUnit={TEMU_WEIGHT_UNIT}
                dimUnit={TEMU_DIM_UNIT}
                netWeightRequired={netWeightRequired}
                onClose={() => setPkgBatchMode(null)}
                onConfirm={(result) => {
                  if (!result?.applyAll && !result?.bySize) {
                    setPkgBatchMode(null);
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    weightUnit: TEMU_WEIGHT_UNIT,
                    dimUnit: TEMU_DIM_UNIT,
                    skcList: result.applyAll
                      ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                      : applyPackageBatchBySize(prev.skcList, result.bySize),
                  }));
                  showToast(
                    result.applyAll
                      ? fixedUi('已批量应用到全部 SKU')
                      : fixedUi('已按尺码批量应用到各颜色的对应尺码行'),
                    'success',
                  );
                  setPkgBatchMode(null);
                }}
              />
            ) : null}
          </div>

          {/* —— 销售发货：半托管填站点/仓/运费；定制品工艺全模式可见 —— */}
          <div id="tpo-pub-sales" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
            <h3>{t('publishChrome.sectionSales')}</h3>

            {skipSiteWarehouseFreight ? (
              <p className="tpo-wizard__inline-hint">
                {officialMode
                  ? (shopType
                    ? t('templateEditor.salesSkipOfficial', { shopType })
                    : t('templateEditor.salesSkipOfficialFallback'))
                  : t('templateEditor.salesSkipShop')}
              </p>
            ) : (officialMode ? (
              <p className="tpo-wizard__inline-hint">
                {t('templateEditor.semiOfficialHint')}
              </p>
            ) : null)}

            <div className="tpo-wizard__form">
              {!skipSiteWarehouseFreight ? (
                <>
                  {(isSemiManaged || shopCaps.usesPublishSiteList || officialMode) ? (
                    <div className={`tpo-field tpo-field--full${fieldErrors.selectedSites ? ' tpo-field--invalid' : ''}`} data-field-error="selectedSites">
                      <span className="tpo-field__label">
                        {t('tiktokPublish.listingSites')}
                        {!officialMode ? <RequiredMark /> : null}
                      </span>
                      <SearchableSelect
                        value={(form.selectedSites || [])[0] || ''}
                        onChange={onSitesChange}
                        placeholder={
                          isSemiManaged
                            ? t('publishSales.pickTemu')
                            : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.pickOrSearch'))
                        }
                        disabled={!isSemiManaged && !officialMode && (metaLoading || siteSelectOptions.length === 0)}
                        emptyText={
                          isSemiManaged
                            ? t('tiktokPublish.noSites')
                            : (metaLoading
                              ? t('tiktokPublish.loading')
                              : (siteSelectOptions.length === 0
                                ? (officialMode ? t('templateEditor.sitesSyncAny') : t('tiktokPublish.noSitesYet'))
                                : t('tiktokPublish.noSitesYet')))
                        }
                        options={siteSelectOptions}
                      />
                      <FieldErrorText message={fieldErrors.selectedSites} />
                    </div>
                  ) : (
                    <div className="tpo-field tpo-field--full">
                      <span className="tpo-field__label">{t('tiktokPublish.listingSites')}</span>
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.sitesNotNeeded')}</p>
                    </div>
                  )}

                  <div className={`tpo-field tpo-field--full${fieldErrors.warehouseId ? ' tpo-field--invalid' : ''}`} data-field-error="warehouseId">
                    <span className="tpo-field__label">
                      {t('tiktokPublish.warehouse')}{!officialMode && (multiWarehouse || isSemiManaged) ? <RequiredMark /> : null}
                    </span>
                    {metaLoading ? (
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
                    ) : officialMode || multiWarehouse || warehouseOptions.length === 0 ? (
                      <>
                        <SearchableSelect
                          value={form.warehouseId}
                          onChange={onWarehouseChange}
                          allowClear={officialMode}
                          placeholder={warehouseOptions.length === 0
                            ? (officialMode ? t('templateEditor.noWhSync') : t('tiktokPublish.noWarehouseOption'))
                            : t('tiktokPublish.pickWarehouse')}
                          emptyText={officialMode
                            ? t('templateEditor.noWhSync')
                            : t('tiktokPublish.noWarehouseOption')}
                          options={warehouseOptions}
                          disabled={warehouseOptions.length === 0 && !officialMode}
                        />
                        {warehouseOptions.length === 0 ? (
                          <p className="tpo-wizard__inline-hint">
                            {officialMode
                              ? t('templateEditor.officialWhCache')
                              : t('templateEditor.noWhData')}
                          </p>
                        ) : multiWarehouse || officialMode ? (
                          <p className="tpo-wizard__inline-hint">
                            {officialMode
                              ? t('templateEditor.officialWhMatch')
                              : t('publishSales.multiWhHint')}
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <p className="tpo-wizard__inline-hint">
                        {t('publishSales.defaultWh', { name: warehouseOptions[0]?.label || form.warehouseId })}
                      </p>
                    )}
                    <FieldErrorText message={fieldErrors.warehouseId} />
                  </div>

                  <div className={`tpo-field tpo-field--full${fieldErrors.freightTemplateId ? ' tpo-field--invalid' : ''}`} data-field-error="freightTemplateId">
                    <span className="tpo-field__label">
                      {t('tiktokPublish.freight')}{isSemiManaged && !officialMode ? <RequiredMark /> : null}
                    </span>
                    {officialMode && !shopId ? (
                      <p className="tpo-wizard__inline-hint">{t('templateEditor.officialFreightLeave')}</p>
                    ) : freightLoading ? (
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.loadingFreight')}</p>
                    ) : freightOptions.length === 0 ? (
                      <p className="tpo-wizard__inline-hint">{t('publishSales.noFreightTemu')}</p>
                    ) : (
                      <SearchableSelect
                        value={form.freightTemplateId}
                        onChange={(v) => update('freightTemplateId', v)}
                        allowClear={!isSemiManaged}
                        placeholder={t('publishSales.pickFreight')}
                        options={freightOptions}
                        placement="top"
                      />
                    )}
                    <FieldErrorText message={fieldErrors.freightTemplateId} />
                  </div>
                </>
              ) : null}

              <div className="tpo-field tpo-field--full">
                <span className="tpo-field__label">
                  {t('publishSales.productMode')} <RequiredMark />
                </span>
                <SearchableSelect
                  value={String(form.isCustomProduct ? 1 : 0)}
                  allowClear={false}
                  onChange={(v) => {
                    const custom = Number(v) === 1 ? 1 : 0;
                    const nextOpts = getTemuShipmentLimitOptions({
                      siteIds: form.selectedSites,
                      customProduct: custom,
                    });
                    setForm((prev) => ({
                      ...prev,
                      isCustomProduct: custom,
                      personalizationSwitch: custom,
                      shipmentLimitSecond: pickTemuShipmentLimitSecond(
                        prev.shipmentLimitSecond,
                        nextOpts,
                      ),
                      ...(custom ? {} : {
                        customFirstType: '',
                        customTechnologyType: '',
                        customTwiceTypes: [],
                      }),
                    }));
                  }}
                  options={TEMU_PRODUCT_MODE_OPTIONS.map((opt) => ({
                    ...opt,
                    label: opt.value === '1' ? t('publishSales.modeCustom') : t('publishSales.modeNormal'),
                  }))}
                  placement="top"
                />
              </div>

              {Number(form.isCustomProduct) === 1 ? (
                <>

                  <div className={`tpo-field tpo-field--full${fieldErrors.customTechnologyType ? ' tpo-field--invalid' : ''}`} data-field-error="customTechnologyType">
                    <span className="tpo-field__label">{t('publishSales.techType')}</span>
                    <SearchableSelect
                      value={String(form.customTechnologyType || '')}
                      allowClear
                      placeholder={t('publishSales.techPh')}
                      onChange={(v) => {
                        const nextType = v == null ? '' : String(v);
                        const nextFirstOpts = listTemuCustomFirstTypeOptions(nextType);
                        setForm((prev) => {
                          const keepFirst = nextFirstOpts.some((o) => String(o.value) === String(prev.customFirstType))
                            ? String(prev.customFirstType || '')
                            : '';
                          const allowed = listTemuCustomTwiceTypeIds(nextType, keepFirst);
                          const allowSet = allowed ? new Set(allowed.map(String)) : null;
                          const twice = (Array.isArray(prev.customTwiceTypes) ? prev.customTwiceTypes : [])
                            .map(String)
                            .filter((id) => (allowSet ? allowSet.has(id) : false));
                          return {
                            ...prev,
                            customTechnologyType: nextType,
                            customFirstType: keepFirst,
                            customTwiceTypes: nextType === '1' ? twice.slice(0, 1) : twice,
                          };
                        });
                      }}
                      options={TEMU_CUSTOM_TECHNOLOGY_TYPE_OPTIONS.map((opt) => ({
                        ...opt,
                        label: opt.value === '2' ? t('publishSales.techCombo') : t('publishSales.techSingle'),
                      }))}
                      placement="top"
                    />
                    <FieldErrorText message={fieldErrors.customTechnologyType} />
                  </div>
                  <div className={`tpo-field tpo-field--full${fieldErrors.customFirstType ? ' tpo-field--invalid' : ''}`} data-field-error="customFirstType">
                    <span className="tpo-field__label">{t('publishSales.firstTech')}</span>
                    <SearchableSelect
                      value={String(form.customFirstType || '')}
                      allowClear
                      placeholder={form.customTechnologyType ? t('publishSales.pickFirst') : t('publishSales.pickTechFirst')}
                      disabled={!form.customTechnologyType}
                      onChange={(v) => {
                        const nextFirst = v == null ? '' : String(v);
                        setForm((prev) => {
                          const allowed = listTemuCustomTwiceTypeIds(prev.customTechnologyType, nextFirst);
                          const allowSet = allowed ? new Set(allowed.map(String)) : null;
                          const twice = (Array.isArray(prev.customTwiceTypes) ? prev.customTwiceTypes : [])
                            .map(String)
                            .filter((id) => (allowSet ? allowSet.has(id) : false));
                          return {
                            ...prev,
                            customFirstType: nextFirst,
                            customTwiceTypes: String(prev.customTechnologyType) === '1'
                              ? twice.slice(0, 1)
                              : twice,
                          };
                        });
                      }}
                      options={customFirstTypeOptions}
                      placement="top"
                    />
                    <FieldErrorText message={fieldErrors.customFirstType} />
                  </div>
                  {customTwiceRequired ? (
                    <div className={`tpo-field tpo-field--full${fieldErrors.customTwiceTypes ? ' tpo-field--invalid' : ''}`} data-field-error="customTwiceTypes">
                      <span className="tpo-field__label">{t('publishSales.secondTech')}</span>
                      <SearchableMultiSelect
                        value={(form.customTwiceTypes || []).map(String)}
                        onChange={(next) => {
                          const list = Array.isArray(next) ? next.map(String) : [];
                          update(
                            'customTwiceTypes',
                            String(form.customTechnologyType) === '1' ? list.slice(0, 1) : list,
                          );
                        }}
                        placeholder={
                          String(form.customTechnologyType) === '2'
                            ? t('publishSales.twiceCombo')
                            : t('publishSales.twiceSingle')
                        }
                        options={customTwiceTypeOptions}
                      />
                      <p className="tpo-wizard__inline-hint">
                        {t('publishSales.twiceHint')}
                      </p>
                      <FieldErrorText message={fieldErrors.customTwiceTypes} />
                    </div>
                  ) : (
                    form.customTechnologyType && form.customFirstType ? (
                      <p className="tpo-wizard__inline-hint">{t('publishSales.noSecond')}</p>
                    ) : null
                  )}
                </>
              ) : null}

              {!skipSiteWarehouseFreight ? (
                <div className={`tpo-field tpo-field--full${fieldErrors.shipmentLimitSecond ? ' tpo-field--invalid' : ''}`} data-field-error="shipmentLimitSecond">
                  <span className="tpo-field__label">
                    {t('tiktokPublish.shipLimit')}{isSemiManaged ? <RequiredMark /> : null}
                  </span>
                  <SearchableSelect
                    value={pickTemuShipmentLimitSecond(form.shipmentLimitSecond, shipmentLimitOptions)}
                    allowClear={false}
                    onChange={(v) => update('shipmentLimitSecond', v)}
                    options={shipmentLimitOptions}
                    placement="top"
                    emptyText={form.selectedSites?.length ? t('publishSales.noShipLimit') : t('publishSales.pickSiteFirst')}
                  />
                  <FieldErrorText message={fieldErrors.shipmentLimitSecond} />
                </div>
              ) : null}
            </div>
          </div>

        </div>

        <div className="tpo-wizard__footer">
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost"
            onClick={() => navigate(
              officialMode && afterSaveNavigate
                ? afterSaveNavigate
                : buildTemuPath('templates/product'),
            )}
            // 加载中允许返回；仅保存中禁用（有全屏遮罩，避免半截写入时跳走）
            disabled={savingTemplate}
          >
            {t('templateEditor.backList')}
          </button>
          <div className="tpo-wizard__footer-right">
            {!officialMode ? (
              <button
                type="button"
                className="tpo-btn tpo-btn--secondary"
                onClick={onPublishFromTemplate}
                disabled={savingTemplate || templateLoading || !templateHydrated || !shopId}
              >
                {t('productTemplate.publish')}
              </button>
            ) : null}
            <button
              type="button"
              className="tpo-btn tpo-btn--primary"
              onClick={onSaveProductTemplate}
              disabled={savingTemplate || templateLoading || !templateHydrated || (!officialMode && !shopId)}
            >
              {savingTemplate ? t('tiktokPublish.saving') : t('shop.save')}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="tpo" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        officialMode={officialMode}
        shopType={shopType}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
    </div>
  );
}
